import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonContent, ModalController, Platform } from '@ionic/angular';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { Subscription, fromEvent, of } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import mixpanel from 'mixpanel-browser';
import { Chart } from 'chart.js';
import { AppState } from '../../app.service';
import { I18n } from '../../i18n.service';
import { ToasterService } from '../../toaster.service';
import { User } from '../../user/user.model';
import { HomeUiService } from '../home-ui.service';
import { WalletService } from '../../wallet/wallet.service';
import { TranslateComponentModalComponent } from '../../components/translate-component-modal/translate-component-modal.component';
import { PRACTICE_LANGUAGES, toVoiceLanguage } from './practice-languages';
import {
  PracticeAnalysis,
  PracticeBotService,
  PracticeCompletedAttempt,
  PracticeCriterionResult,
  PracticeDefaults,
  PracticeEndedReason,
  PracticeRefinementTurn,
  PracticeScenario,
  PracticeSessionStatus,
  PracticeSettings,
  PracticeTranscriptTurn,
  PracticeVoiceOption,
  PRACTICE_SCORE_TARGET,
} from '../practice-bot.service';
import { PracticeVoiceCallService } from '../practice-voice-call.service';

type PracticeBotScreen = 'main' | 'settings' | 'voice' | 'feedback' | 'final' | 'feebacknodata';

// The screens Back can return to. The voice call, the feedback wait and the report are all
// transient, so none of them is ever a destination.
const BACKABLE_SCREENS: PracticeBotScreen[] = ['main', 'settings'];

// How long the "Preparing your feedback" screen shows before the score appears.
// Fixed for now — swap for the real generation call when there is one.
const FEEDBACK_LOADING_MS = 7000;

/**
 * Attempts fetched per request on the main screen. The endpoint's own cap is 50; twenty is a page
 * the learner can absorb and enough that most actions need only one.
 */
const ATTEMPTS_PAGE_SIZE = 20;

/**
 * How many extra pages the report will chase when a page brings no attempt it can show.
 *
 * Bounded because the condition that triggers it — too few completed attempts held — can stay true
 * however many pages arrive, and an empty recordings list is a smaller problem than walking back
 * through a learner's entire history to fill it.
 */
const ATTEMPT_FILL_CHASES = 3;

// `settings/refine` rewrites the settings from an instruction and rejects a request without one.
// When the learner only edited the form fields, this asks it to hand back what we sent.
const KEEP_SETTINGS_PROMPT = 'Keep the persona, scenario, and rubric exactly as provided.';

// The score the meter marks as the bar to clear. Presentational only — the analysis carries
// no target of its own.
// The readiness threshold lives on the service now, shared with the history list's ready chip.

// How long to wait for one attempt's analysis before giving the learner the link back. A
// completed session is a plain read, so anything past this is the request having stalled.
const SUMMARY_TIMEOUT_MS = 20000;

// Y-axis ticks on the trend chart, as the design marks them.
const TREND_TICKS = [0, 40, 80];

/**
 * Draws each trend point's score above its dot, always — not only on hover — and keeps the
 * x-axis labels from packing closer than a minimum pixel gap.
 *
 * This vendored Chart.js (2.3.0) has no per-chart `config.plugins`: `Chart.plugins` is a single
 * global registry shared by every chart in the app (including the doughnut chart in
 * StatComponent), so this is registered once here rather than passed through the chart config,
 * and the `pbShowValueLabels` / `pbXAxisMinTickSpacingPx` option flags keep it from touching
 * charts that don't ask for it.
 */
Chart.plugins.register({
  beforeUpdate(chart: any) {
    const minSpacing = chart.options?.pbXAxisMinTickSpacingPx;
    const xAxisOptions = chart.options?.scales?.xAxes?.[0];
    // chart.chart.width is the width the chart was built at, so this follows each rebuild.
    const width = chart.chart?.width;
    if (!minSpacing || !xAxisOptions || !width) {
      return;
    }
    xAxisOptions.ticks = xAxisOptions.ticks || {};
    xAxisOptions.ticks.maxTicksLimit = Math.max(2, Math.floor(width / minSpacing) + 1);
  },
  // Also no working `layout.padding` in this Chart.js build (core.layoutService.js hardcodes it to
  // 0), so there's no built-in way to keep the last point's centered value label from having its
  // far half drawn past the canvas edge and clipped. Shrinking the x-scale's own box by a fixed
  // pixel amount — after layout has placed it (afterScaleUpdate) but before datasets are plotted
  // against it (updateDatasets, right after) — reserves exact margin there instead, whatever the
  // point spacing happens to be.
  afterScaleUpdate(chart: any) {
    const marginPx = chart.options?.pbXAxisRightMarginPx;
    const xScale = chart.scales?.['x-axis-0'];
    if (marginPx && xScale) {
      xScale.right -= marginPx;
      xScale.width -= marginPx;
      if (chart.chartArea) {
        chart.chartArea.right -= marginPx;
      }
    }
    const topPaddingPx = chart.options?.pbYAxisTopPaddingPx;
    const yScale = chart.scales?.['y-axis-0'];
    if (topPaddingPx && yScale) {
      yScale.paddingTop = (yScale.paddingTop || 0) + topPaddingPx;
    }
  },
  afterDatasetsDraw(chart: any) {
    if (!chart.options?.pbShowValueLabels) {
      return;
    }
    const data = chart.data.datasets[0].data;
    // The hook receives the chart Controller, not the base chart instance — the 2D context lives
    // one level down, on the wrapper it was built from (`chart.chart.ctx`), not `chart.ctx` itself.
    const ctx = chart.chart.ctx;
    ctx.save();
    ctx.font = '600 12px Inter';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    chart.getDatasetMeta(0).data.forEach((point: any, index: number) => {
      // Same no-op layout.padding problem, vertically: a point close enough to yMax (e.g. a score
      // of 99 against the 0–100 axis) would otherwise have its label drawn partly above the canvas.
      const y = Math.max(point._model.y - 12, 12);
      ctx.fillText(String(data[index]), point._model.x, y);
    });
    ctx.restore();
  }
});

/** Trend chart geometry, in percentages of the plot box. */
interface PracticeTrendChart {
  /** Polyline points for the 0–100 × 0–100 viewBox the plot is drawn in. */
  line: string;
  points: Array<{ x: number; y: number; score: number; label: string }>;
  ticks: Array<{ value: number; y: number }>;
}

/** One row of the preview screen's timeline: something said, or a practice attempt. */
interface PracticeTimelineItem {
  kind: 'turn' | 'attempt';
  /** Epoch ms used only for ordering. */
  time: number;
  turn?: PracticeRefinementTurn;
  attempt?: { id: string; at: string; score: number | null; cancelled: boolean };
}

/** One delivery metric under the score. */
interface PracticeDeliveryChip {
  text: string;
  tone: 'green' | 'pink';
  icon: 'pace' | 'pause' | 'speech';
}

/**
 * One point of feedback. The analysis returns these as situation / behavior / impact, with a
 * suggestion on the ones to improve; `label` carries the criterion name of the documented shape.
 */
interface PracticeFeedbackPoint {
  situation: string;
  behavior: string;
  impact: string;
  suggestion: string;
  label: string;
}

// The settings form, in the flat shape `settings/refine` takes.
interface PracticeSettingsForm extends PracticeSettings {
  persona_name: string;
  persona_description: string;
  scenario_text: string;
  rubric_text: string;
  learner_goal: string;
  voice_gender: 'male' | 'female';
  voice_tone: string;
  /** ISO 639-1 code. Empty means the Cycle's own language. */
  language: string;
  /** Free text, max 100 — "British", "South Indian". */
  accent: string;
}

const EMPTY_SETTINGS_FORM: PracticeSettingsForm = {
  persona_name: '',
  persona_description: '',
  scenario_text: '',
  rubric_text: '',
  learner_goal: '',
  voice_gender: 'male',
  // A tone from the taxonomy the rows are keyed by. 'assertive' used to sit here, from a list that
  // no longer exists — it matched no row, so the picker opened with nothing selected while the
  // session went ahead on whatever voice the backend fell back to.
  voice_tone: 'neutral-balanced',
  language: '',
  accent: '',
};

/** One row of the voice tone picker on the settings screen. */
interface PracticeVoiceToneOption {
  value: string;
  label: string;
  /**
   * True for the three tones that exist only as demo clips.
   *
   * They map to onyx/nova/fable, which the Realtime API refuses — offering one as a session voice
   * mints a call that fails at start. They can still be listened to, which is why they stay in the
   * list rather than being deleted from it.
   */
  demoOnly?: boolean;
}

/**
 * The shared tone taxonomy — all thirteen. The server's `available_voices` is the authority on
 * which of them can start a session; this stands in until that response arrives.
 */
/**
 * How far before the end of a demo clip the equaliser bars settle.
 *
 * The clips are generated speech and end with a moment of silence; without this the bars waved on
 * after the voice had finished.
 */
const DEMO_TAIL_SECONDS = 0.35;

const VOICE_TONE_OPTIONS: PracticeVoiceToneOption[] = [
  { value: 'neutral-balanced', label: 'Neutral, balanced' },
  { value: 'clear-articulate', label: 'Clear, articulate' },
  { value: 'smooth-melodic', label: 'Smooth, melodic' },
  { value: 'vibrant-warm', label: 'Vibrant, warm' },
  { value: 'resonant-clear', label: 'Resonant, clear' },
  { value: 'expresive-warm', label: 'Expresive, warm' },
  { value: 'bright-energetic', label: 'Bright, energetic' },
  { value: 'deep-authoratative', label: 'Deep, authoritative', demoOnly: true },
  { value: 'measure-considered', label: 'Measure, considered' },
  { value: 'bright-cheerful', label: 'Bright, cheerful', demoOnly: true },
  { value: 'expresive-poetic', label: 'Expresive, poetic', demoOnly: true },
  { value: 'warm-natural', label: 'Warm, natural' },
  { value: 'warm-grounded', label: 'Warm, grounded' },
];

@Component({
  selector: 'app-practice-bot-modal',
  templateUrl: './practice-bot-modal.component.html',
  styleUrls: ['./practice-bot-modal.component.scss'],
})
export class PracticeBotModalComponent implements OnInit, OnDestroy, AfterViewChecked {
  action: any;
  screen: PracticeBotScreen = 'main';
  /** Where the report's Back button returns to — the screen it was opened from. */
  private returnScreen: PracticeBotScreen = 'main';
  /**
   * componentProps: open straight onto this attempt's report instead of the preview. Set by the
   * in-step coach's "View Feedback", which already knows which session the learner picked.
   */
  openSummarySessionId = '';
  /**
   * True while the report is the only screen the learner has seen in this modal. Back then means
   * leaving the modal — the preview belongs to a journey they did not take.
   */
  private openedOnSummary = false;
  // showScene = false;
  isEndModalOpen = false;
  practiceSessionId: string;
  scenario: PracticeScenario;
  customContext = '';
  /** What the preview screen's scenario card shows: the settings as last saved. */
  settings: PracticeSettingsForm = { ...EMPTY_SETTINGS_FORM };
  /**
   * What the settings form edits. A copy, so clearing a field and going Back leaves the scenario
   * card alone — only Save commits it.
   */
  settingsDraft: PracticeSettingsForm = { ...EMPTY_SETTINGS_FORM };
  availableVoices: PracticeVoiceOption[] = [];
  isLoadingDefaults = false;
  isSavingSettings = false;
  private defaultsLoaded = false;
  /** The preview screen's "Customize scenario" box and the conversation it produces. */
  refinementInput = '';
  refinementHistory: PracticeRefinementTurn[] = [];
  isRefining = false;
  private timelineCache: {
    turns: PracticeRefinementTurn[];
    completed: any[];
    cancelled: PracticeCompletedAttempt[];
    showAttempts: boolean;
    items: PracticeTimelineItem[];
  };
  /** True once the learner has refined or saved: freshly generated defaults must not clobber it. */
  private hasShapedSettings = false;
  transcript: PracticeTranscriptTurn[] = [];
  analysis: PracticeAnalysis;
  /**
   * The `status` of the last practice response that carried one, stored verbatim for the UI to
   * key an icon off. Documented values: 'active' · 'ended_awaiting_audio' · 'analyzing' ·
   * 'complete' · 'failed'; '' until the first response arrives, and cleared when a new session
   * starts so a previous attempt's status cannot linger. A value outside that set is kept as
   * given rather than mapped onto one of them.
   */
  practiceStatus: PracticeSessionStatus | '' = '';
  /** Which session the shown analysis belongs to — the recording is fetched against it. */
  analysisSessionId = '';

  /**
   * The report's own list of attempts: three at a time behind See more, always open. Short enough
   * to sit under the feedback without competing with it, and the attempt being shown is in the list
   * (outlined) rather than left out — the learner needs to see where they are among the others.
   */
  private readonly RECORDINGS_STEP = 3;
  recordingsShown = this.RECORDINGS_STEP;
  /** The recording being deleted, so its row can say so and not be tapped twice. */
  deletingRecordingId = '';
  /** Meter target, exposed so the legend and the marker read from one place. */
  readonly scoreTarget = PRACTICE_SCORE_TARGET;
  @ViewChild('playbackAudio') playbackAudio: ElementRef<HTMLAudioElement>;
  /** The trend chart's canvas — only in the DOM while `*ngIf="trend.length"` keeps its section mounted. */
  @ViewChild('trendCanvas') trendCanvasRef?: ElementRef<HTMLCanvasElement>;
  /** The canvas's scrollable wrapper — widened past the card so points keep a readable spacing. */
  @ViewChild('trendScroll') trendScrollRef?: ElementRef<HTMLDivElement>;
  private trendChartInstance?: Chart;
  /** Last trend data the canvas was drawn from, so ngAfterViewChecked can skip redraws when nothing changed. */
  private trendChartSignature = '';
  /**
   * Watches the chart's box so the chart is rebuilt once it has real dimensions, including when no
   * change detection runs at that moment. Chart.js 2.3's own resize detection (a hidden iframe's
   * `onresize`) doesn't fire reliably on iOS, so the chart is sized by syncTrendChart instead.
   */
  private trendResizeObserver?: ResizeObserver;
  /** The element trendResizeObserver is attached to, so a remounted section gets a fresh observer. */
  private trendObservedEl?: HTMLElement;
  playbackUrl = '';
  hasPlaybackAudio = true;
  isLoadingPlayback = false;
  /**
   * The learner has asked for playback and no sound has arrived yet.
   *
   * Only from a tap: the URL is also fetched when the report opens, and marking the control busy for
   * that would have it spinning at something nobody asked for. Bound to the real states — the URL
   * round trip, then the browser loading the file until `playing` fires — with no floor under it, so
   * a recording ready to go starts with no spinner at all.
   */
  playbackStarting = false;
  /** A play asked for while the URL was still on its way, to be honoured when it lands. */
  private playWhenLoaded = false;
  isPlaybackPlaying = false;
  playbackSeconds = 0;
  playbackDurationSeconds = 0;
  /** The seek-past-the-end trick in onPlaybackLoaded, run at most once per loaded recording. */
  private playbackDurationProbed = false;
  /**
   * The server told us how long the recording is, so the media does not need asking.
   *
   * Its value is the authority: the element reports Infinity on a MediaRecorder container until
   * something indexes it, and 0 while a stream is still being sized — both of which read as "no
   * length" and blank the scrubber.
   */
  private playbackDurationFromServer = false;
  isStarting = false;
  isAnalyzing = false;
  isEnding = false;
  errorMessage = '';
  private micPromptResumeGraceUntil = 0;
  private static readonly MIC_PROMPT_RESUME_GRACE_MS = 2000;
  private endedSessionIds = new Set<string>();
  /** The learner discarded this attempt — a late ended$ must not start an end/analysis path. */
  private isDiscarded = false;
  completedAttempts: PracticeCompletedAttempt[] = [];
  /** Attempts that ended without a report: status `failed`. No summary to link to. */
  cancelledAttempts: PracticeCompletedAttempt[] = [];
  lastPracticedAt: string;
  lastSessionId: string;
  lastScore: number | null = null;
  isLoadingSummary = false;
  openingSummaryId = '';
  /** Bot audio sink — the remote WebRTC track is attached here so the learner can hear it. */
  @ViewChild('botAudio') botAudio: ElementRef<HTMLAudioElement>;
  /** "We're picking up background noise" — set once per call by noiseWarning$. */
  showNoiseHint = false;
  /**
   * Height of the on-screen keyboard, as scroll room under the settings form.
   *
   * iOS needs this because config.xml sets `KeyboardResize: false`: the keyboard is drawn OVER the
   * WebView without resizing it, so the page never learns it has less room and the field the learner
   * tapped stays underneath. Padding the form gives the scroller somewhere to go — a field that is
   * last in the container cannot otherwise be scrolled up at all.
   */
  keyboardOffset = 0;
  keyboardShowing = false;
  keyboardDismissing = false;
  isCordovaMobile = false;
  @ViewChild('transcriptLog') transcriptLog: ElementRef<HTMLElement>;
  /** The voice screen's ion-content — the only handle on the element that actually scrolls. */
  @ViewChild('voiceContent') voiceContent: IonContent;
  private transcriptScroller: HTMLElement;
  /**
   * True while a finger is down on the voice screen. A live turn can stream in fast enough that a
   * forced scrollToBottom() lands mid-gesture and fights it, so any auto-scroll is skipped for as
   * long as this is true — the learner's own scroll always wins over a still-arriving turn.
   */
  private isVoiceContentTouchActive = false;
  /** The preview screen's ion-content — refinement turns and practice attempts scroll here. */
  @ViewChild('mainContent') mainContent: IonContent;
  /** The report's scroller, so switching to another recording starts at the top of its feedback. */
  @ViewChild('reportContent') reportContent: IonContent;
  /** The single player the recordings list shares. */
  @ViewChild('rowAudio') rowAudio: ElementRef<HTMLAudioElement>;
  rowPlaybackUrl = '';
  @ViewChild('mainTimeline') mainTimeline: ElementRef<HTMLElement>;
  isMuted = false;
  /** The Scene button's popover triangle only makes sense while that popover is actually open. */
  isSceneInfoOpen = false;
  callElapsedMs = 0;
  maxDurationSeconds = 600;
  liveTurn: { role: 'user' | 'assistant'; text: string };
  private feedbackTimer: any;
  private callTicker: any;
  private botAudioRetry?: () => void;
  private subscriptions = new Subscription();
  currentUser: User;
  isMobile: boolean;
  /**
   * Only what the voice bot can speak — not the translate picker's 105, which are for translating
   * the UI. A language it cannot speak would be accepted here and then silently dropped back to
   * the Cycle's own.
   */
  availableLanguages = PRACTICE_LANGUAGES;
  pathLanguage = 'en';
  /**
   * The tones the picker offers.
   *
   * Driven by `available_voices` once defaults answers — that response is the authority on which
   * tones can actually start a session, and it already excludes the three demo-only ones. Until
   * then, the taxonomy minus those three, so the list is never wrong in the direction that mints
   * a call the Realtime API will refuse.
   */
  voiceToneOptions: PracticeVoiceToneOption[] = VOICE_TONE_OPTIONS.filter((option) => !option.demoOnly);

  /**
   * Rebuilds the picker's rows from what the server advertised.
   *
   * A field rebuilt once, not a getter: a getter hands the template a NEW array on every
   * change-detection pass, so every row is torn down and rebuilt on each one — which locks this
   * screen up.
   */
  private rebuildVoiceToneOptions(): void {
    const advertised = (this.availableVoices || [])
      .map((voice) => ({ value: voice?.value || voice?.tone || '', label: voice?.label || '' }))
      .filter((option) => !!option.value);
    this.voiceToneOptions = advertised.length
      ? advertised
      : VOICE_TONE_OPTIONS.filter((option) => !option.demoOnly);
    this.ensureVoiceToneSelected();
  }

  /**
   * Keeps the picker showing the voice the session would actually use.
   *
   * A session is created with `resolveVoiceId`, which falls back to the first voice on offer when
   * the chosen tone matches none — so a tone that is not in the list is not "no voice", it is a
   * voice the learner cannot see. That happened on every first open, and again whenever the server
   * advertises a list that a saved choice predates.
   *
   * A choice that IS on offer is never touched.
   */
  private ensureVoiceToneSelected(): void {
    const offered = this.voiceToneOptions.some((option) => option.value === this.settings.voice_tone);
    if (offered) { return; }
    const first = this.voiceToneOptions[0]?.value || '';
    if (!first) { return; }
    this.settings = { ...this.settings, voice_tone: first };
    // The form may already be open — the defaults response lands while the learner is reading it.
    this.settingsDraft = { ...this.settingsDraft, voice_tone: first };
  }

  /** The clip currently playing in the picker, so its row can show it. */
  playingToneValue = '';
  /** The tone whose clip is being fetched or loaded — the row shows a spinner rather than nothing. */
  loadingToneValue = '';
  /** One clip at a time: a second preview replaces the first rather than talking over it. */
  private demoAudio: HTMLAudioElement | null = null;
  /**
   * Which preview the learner is waiting on. Bumped by every stop, so anything still in flight from
   * an earlier tap — a signed URL that has not arrived, a `play()` promise that has not settled —
   * can tell that it is no longer wanted and say nothing.
   */
  private demoRequestId = 0;
  /** Presigned demo URLs already fetched this run, keyed tone|language. Good for ~5 days. */
  private demoUrls = new Map<string, string>();
  /** The code last saved via the translate modal, e.g. 'fr' — drives the Language field's display. */
  selectedLanguage: string;

  constructor(
    private modalController: ModalController,
    private practiceBotService: PracticeBotService,
    private voiceCall: PracticeVoiceCallService,
    private cdRef: ChangeDetectorRef,
    private i18n: I18n,
    private toasterService: ToasterService,
    private platform: Platform,
    private androidPermissions: AndroidPermissions,
    private keyboard: Keyboard,
    private elRef: ElementRef,
    private appState: AppState,
    public homeUIService: HomeUiService,
    private walletService: WalletService
  ) {
    this.currentUser = this.appState.get('currentUser');
    this.isMobile = this.appState.get('isMobile');
    this.pathLanguage = this.appState.get('currentPath')?.language || 'en';
    this.selectedLanguage = this.appState.get('selectedLanguage');
    this.subscriptions.add(
      this.voiceCall.ended$.subscribe((reason) => this.finishCall(reason))
    );
    this.subscriptions.add(
      this.voiceCall.remoteStream$.subscribe((stream) => this.attachBotAudio(stream))
    );
    this.subscriptions.add(
      // Turns keep being committed and transcribed to nothing: say so rather than leaving the
      // learner wondering why the coach has stopped answering. Non-blocking — the call continues.
      this.voiceCall.noiseWarning$.subscribe(() => {
        this.showNoiseHint = true;
        this.cdRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.voiceCall.transcriptChange$.subscribe((transcript) => {
        const shouldAutoScroll = this.isFollowingTranscript();
        this.transcript = transcript;
        this.cdRef.detectChanges();
        if (shouldAutoScroll) { this.scrollTranscriptToBottom(); }
      })
    );
    this.subscriptions.add(
      this.voiceCall.liveTurn$.subscribe((turn) => {
        const shouldAutoScroll = this.isFollowingTranscript();
        this.liveTurn = turn as any;   // null between turns
        this.cdRef.detectChanges();
        if (shouldAutoScroll) { this.scrollTranscriptToBottom(); }
      })
    );
    this.subscriptions.add(
      this.voiceCall.error$.subscribe((error) => console.log('Practice voice call error', error))
    );
  }

  ngOnInit() {
    this.registerKeyboardEvents();
    this.installDismissGuard();
    this.clearStaleErrorOnResume();
    this.loadLastPracticeSession();
    this.restoreRefinementState();
    // The preview screen's scenario card and the settings form both render these.
    this.loadPracticeDefaults();

    // Opened on a specific attempt: land on the report screen straight away, so the preview it
    // was not opened for never flashes up while the analysis is being fetched.
    if (this.openSummarySessionId) {
      this.openedOnSummary = true;
      this.screen = 'final';
      this.openPerformanceSummary(this.openSummarySessionId);
    } else {
      // A restored conversation can already overflow the preview screen — open on its newest
      // line rather than its oldest.
      this.scrollMainToBottom();
    }
  }

  /**
   * Re-opening the modal builds a new component, so anything the learner shaped last time lives
   * in the service, not here. Restore it before the defaults land.
   */
  private restoreRefinementState() {
    const screenContentId = this.screenContentId;
    if (!screenContentId) { return; }

    const fingerprint = this.actionFingerprint;
    const sameRun = this.practiceBotService.getRefinementState(screenContentId);
    const stored = this.practiceBotService.getStoredConversation(screenContentId);
    const shapedFor = sameRun?.actionFingerprint ?? stored?.actionFingerprint;

    // The action has been rewritten since these were shaped — customising keeps the same
    // screen_content_id, so only the wording tells them apart. Drop them and let `defaults`
    // supply a scenario for the action as it reads now. A record with no fingerprint predates
    // this check, so it is kept rather than thrown away on the deploy that introduces it.
    if ((sameRun || stored) && shapedFor && shapedFor !== fingerprint) {
      // The scenario text is stale, but the voice is about how the persona sounds rather than
      // what the action says, so the pick survives the rewrite.
      const previous: any = sameRun?.settings ?? stored?.settings ?? {};
      this.practiceBotService.clearRefinementState(screenContentId);
      if (previous.voice_tone) {
        this.settings = {
          ...this.settings,
          voice_gender: previous.voice_gender || this.settings.voice_gender,
          voice_tone: previous.voice_tone,
        };
      }
      return;
    }

    if (sameRun) {
      this.settings = { ...EMPTY_SETTINGS_FORM, ...sameRun.settings } as PracticeSettingsForm;
      this.refinementHistory = [...(sameRun.history ?? [])];
      this.customContext = sameRun.customContext ?? '';
      this.hasShapedSettings = true;
      return;
    }

    if (stored) {
      this.refinementHistory = [...(stored.history ?? [])];
      this.customContext = stored.customContext ?? '';
      // What the learner saved outranks what `defaults` answers with — that is the admin's
      // authored version, and it should only show when the learner has none of their own.
      // "Reset to default" clears this record, which is how newer admin content gets through.
      if (stored.settings) {
        this.settings = { ...EMPTY_SETTINGS_FORM, ...stored.settings } as PracticeSettingsForm;
        this.hasShapedSettings = true;
      }
    }
  }

  /**
   * Identifies the action wording the practice settings were shaped against. A customised
   * micro-action keeps its screen_content_id, so the text is the only thing that changes.
   */
  private get actionFingerprint(): string {
    const source = `${this.action?.title ?? ''}|${this.action?.text ?? this.action?.description ?? ''}`;
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
      hash = ((hash << 5) - hash + source.charCodeAt(i)) | 0;   // stable across runs, compact to store
    }
    return String(hash);
  }

  /** Hands the conversation and its settings to the service, which outlives this modal. */
  private persistRefinementState() {
    const screenContentId = this.screenContentId;
    if (!screenContentId) { return; }

    this.hasShapedSettings = true;
    this.practiceBotService.saveRefinementState(screenContentId, {
      settings: { ...this.settings },
      history: [...this.refinementHistory],
      customContext: this.customContext,
      actionFingerprint: this.actionFingerprint,
    });
  }

  /**
   * "Reset to default" — throw away everything the learner shaped, on the server as well as
   * here, then re-read the admin's setup.
   */
  resetToDefaults() {
    const screenContentId = this.screenContentId;
    if (!screenContentId || this.isLoadingDefaults) { return; }

    this.isLoadingDefaults = true;   // the button reads "Resetting..." off this
    this.errorMessage = '';
    this.subscriptions.add(
      this.practiceBotService.deletePracticeSettings(screenContentId).subscribe(
        () => this.applySettingsReset(screenContentId),
        (error) => {
          // Nothing saved to delete leaves exactly the state a delete would have produced.
          if (error?.status === 404) { this.applySettingsReset(screenContentId); return; }
          // Otherwise leave everything alone: the server still holds the learner's version, and
          // re-reading `defaults` would only hand it straight back.
          this.isLoadingDefaults = false;
          this.showError(this.describeSettingsError(error));
          this.cdRef.detectChanges();
        }
      )
    );
  }

  /** The server has forgotten the saved settings; drop what is held here and re-read defaults. */
  private applySettingsReset(screenContentId: number | string) {
    this.practiceBotService.clearRefinementState(screenContentId);
    this.hasShapedSettings = false;
    // The conversation shaped the scenario being thrown away, so it goes with it.
    this.refinementHistory = [];
    this.refinementInput = '';
    this.customContext = '';
    // loadPracticeDefaults() bails out while this is set, and sets it again itself.
    this.isLoadingDefaults = false;
    this.loadPracticeDefaults();
  }

  /**
   * GET /practice/defaults — the generated starting point, also what "Reset to default" runs.
   * Creates no session.
   */
  loadPracticeDefaults() {
    const screenContentId = this.screenContentId;
    if (!screenContentId || this.isLoadingDefaults) { return; }

    this.isLoadingDefaults = true;
    this.errorMessage = '';
    this.subscriptions.add(
      this.practiceBotService.getPracticeDefaults(screenContentId).subscribe(
        (response) => {
          this.isLoadingDefaults = false;
          this.defaultsLoaded = true;
          this.availableVoices = response?.available_voices ?? [];
          // The server's list is the authority on which tones can start a session.
          this.rebuildVoiceToneOptions();
          // Settings shaped earlier in this run stand: they are already the refined ones, and
          // re-applying the response would only risk undoing them. resetToDefaults() drops that
          // flag first, so Reset still takes the fresh values. The conversation is never touched
          // here — after a reload it has just been restored from storage.
          if (!this.hasShapedSettings) {
            this.applyPracticeSettings(response);
          }
          // Reset lands here with the form open, so the draft follows the new values.
          this.settingsDraft = { ...this.settings };
          this.cdRef.detectChanges();
          // The generated scenario text can be long — it lands after the initial render's scroll
          // already ran against the shorter "Generating…" placeholder, so redo it here too.
          this.scrollMainToBottom();
        },
        (error) => {
          this.isLoadingDefaults = false;
          this.showError(this.describeSettingsError(error));
          this.cdRef.detectChanges();
        }
      )
    );
  }

  /**
   * The preview screen's "Customize scenario" box — each message is one `settings/refine` round
   * trip, so the scenario card above and the conversation below both move with it. No session.
   */
  sendRefinement() {
    const prompt = this.refinementInput.trim();
    const screenContentId = this.screenContentId;
    if (!prompt || !screenContentId || this.isRefining) { return; }

    // Empty the box as the message goes out, the way a chat does.
    this.refinementInput = '';
    this.refinementHistory = [
      ...this.refinementHistory,
      { role: 'user', text: prompt, at: new Date().toISOString() },
    ];
    this.isRefining = true;
    this.errorMessage = '';
    this.scrollMainToBottom();

    const before = { ...this.settings };
    this.subscriptions.add(
      this.practiceBotService
        .refinePracticeSettings({
          ...this.currentSettingsPayload(),
          screen_content_id: screenContentId,
          refinement_prompt: prompt,
        })
        .subscribe(
          (response) => {
            this.isRefining = false;
            // Logged because a field the endpoint leaves out reads on screen as the instruction
            // having been ignored — this shows straight away which ones came back.
            console.log('[practice] refine returned:', Object.keys(response || {}));
            this.applyPracticeSettings(response);
            // refine answers with settings, not prose, so the reply is what actually moved.
            this.refinementHistory = [
              ...this.refinementHistory,
              { role: 'coach', text: this.describeSettingsChange(before), at: new Date().toISOString() },
            ];
            // Carry every instruction into session creation too, so the call the learner
            // practises against is built from the same asks they made here.
            this.customContext = [this.customContext, prompt].filter(Boolean).join('\n');
            // Survives closing the modal — nothing about this lives server-side.
            this.persistRefinementState();
            this.cdRef.detectChanges();
            this.scrollMainToBottom();
          },
          (error) => {
            this.isRefining = false;
            // Nothing was applied, so drop the message and hand the text back for a retry.
            this.refinementHistory = this.refinementHistory.slice(0, -1);
            this.refinementInput = prompt;
            this.showError(this.describeSettingsError(error));
            this.cdRef.detectChanges();
          }
        )
    );
  }

  /**
   * The coach's side of the conversation. The endpoint returns no prose, so name the fields that
   * actually changed rather than echoing the whole scenario the card above already shows.
   */
  private describeSettingsChange(before: PracticeSettingsForm): string {
    const changed: string[] = [];
    if (before.scenario_text !== this.settings.scenario_text) {
      changed.push(this.i18n.t('scenario'));
    }
    if (before.persona_name !== this.settings.persona_name
      || before.persona_description !== this.settings.persona_description) {
      changed.push(this.i18n.t('persona'));
    }
    if (before.rubric_text !== this.settings.rubric_text) {
      changed.push(this.i18n.t('rubric'));
    }
    if (before.learner_goal !== this.settings.learner_goal) {
      changed.push(this.i18n.t('goal'));
    }
    if (!changed.length) {
      return this.i18n.t('No change — the scenario above already covers that.');
    }
    return this.i18n.t('Updated the %s above.').replace('%s', changed.join(' · '));
  }

  /** Stable identity for *ngFor so existing lines are not re-rendered on every new message. */
  trackRefinement(index: number) {
    return index;
  }

  /**
   * POST /practice/settings/refine — sends the form as-is plus the learner's instruction, then
   * shows the refined scenario back on the preview screen. Creates no session.
   */
  savePracticeSettings() {
    const screenContentId = this.screenContentId;
    if (!screenContentId || this.isSavingSettings) { return; }

    this.isSavingSettings = true;
    this.errorMessage = '';

    // `refine` takes no voice fields, so the voice pick is purely local: commit and store it
    // now rather than letting it ride on whether the request succeeds.
    this.settings = {
      ...this.settings,
      voice_gender: this.settingsDraft.voice_gender,
      voice_tone: this.settingsDraft.voice_tone,
    };
    this.persistRefinementState();

    this.subscriptions.add(
      this.practiceBotService
        .refinePracticeSettings({
          ...this.currentSettingsPayload(this.settingsDraft),
          screen_content_id: screenContentId,
          // Save carries form edits, not an instruction — the conversation on the preview
          // screen is where instructions are sent.
          refinement_prompt: KEEP_SETTINGS_PROMPT,
        })
        .subscribe(
          (response) => {
            this.isSavingSettings = false;
            // The learner's edits land first, then whatever the response rewrote — a field the
            // response leaves out keeps what they typed rather than reverting.
            this.settings = { ...this.settings, ...this.settingsDraft };
            this.applyPracticeSettings(response);
            this.settingsDraft = { ...this.settings };
            this.persistRefinementState();
            // Back to the preview, which now shows the refined scenario.
            this.showScreen('main');
            this.cdRef.detectChanges();
          },
          (error) => {
            this.isSavingSettings = false;
            this.showError(this.describeSettingsError(error));
            this.cdRef.detectChanges();
          }
        )
    );
  }

  /**
   * Both endpoints answer with the same settings, `defaults` nested and `refine` flat, so read
   * each field from whichever key is present and keep the current value when neither is.
   */
  private applyPracticeSettings(response: PracticeDefaults | PracticeSettings) {
    if (!response) { return; }
    const source: any = response;
    // A field the endpoint omits — or returns blank — must leave what is on screen alone, or a
    // partial response would empty the scenario card instead of refining it.
    const pick = (...candidates: any[]): string =>
      candidates.find((value) => typeof value === 'string' && value.trim().length > 0) ?? '';
    this.settings = {
      ...this.settings,
      persona_name: pick(source.persona_name, source.persona?.name, this.settings.persona_name),
      persona_description: pick(
        source.persona_description, source.persona?.description, this.settings.persona_description
      ),
      scenario_text: pick(source.scenario_text, source.scenario, this.settings.scenario_text),
      rubric_text: pick(source.rubric_text, source.rubric?.text, this.settings.rubric_text),
      learner_goal: pick(source.learner_goal, this.settings.learner_goal),
      // Sticky, and blank means reuse — so an endpoint that answers without them (or with "")
      // leaves whatever is on screen alone. Reset lands here too, and deliberately keeps them:
      // clearing the persona does not clear the language or the accent.
      language: pick(source.language, this.settings.language),
      accent: pick(source.accent, this.settings.accent),
    };
  }

  /**
   * The settings as `refine` takes them. It rewrites from what we send, so every field the
   * learner can see has to go with the request — a goal we withhold is one it cannot revise.
   */
  /**
   * Takes the language and accent the session resolved back into the form.
   *
   * Only when the server actually names them: a response without them means nothing changed, and
   * blanking the fields would lose a saved value the next request would have reused.
   */
  private applyResolvedVoiceSettings(scenario: any): void {
    const language = (scenario?.language || '').trim();
    const accent = (scenario?.accent || '').trim();
    if (!language && !accent) { return; }
    this.settings = {
      ...this.settings,
      language: language || this.settings.language,
      accent: accent || this.settings.accent,
    };
    this.settingsDraft = { ...this.settingsDraft, language: this.settings.language, accent: this.settings.accent };
  }

  private currentSettingsPayload(source: PracticeSettingsForm = this.settings): PracticeSettings {
    return {
      language: source.language,
      accent: source.accent,
      persona_name: source.persona_name,
      persona_description: source.persona_description,
      scenario_text: source.scenario_text,
      rubric_text: source.rubric_text,
      learner_goal: source.learner_goal,
    };
  }

  /** Avatar letter on the persona card. */
  get personaInitial(): string {
    return (this.settings.persona_name || '').trim().charAt(0).toUpperCase();
  }

  /** Display name for the Language field — the same list the translate modal shows. */
  /** The Cycle's language in the spelling the voice API takes, which is what the field defaults to. */
  get voicePathLanguage(): string {
    return toVoiceLanguage(this.pathLanguage);
  }

  /**
   * The language this session would run in: the learner's own pick, or the Cycle's when they have
   * not made one. '' when neither is a language the bot can speak.
   */
  get voiceSessionLanguage(): string {
    return toVoiceLanguage(this.settingsDraft?.language) || this.voicePathLanguage;
  }

  get selectedLanguageLabel(): string {
    // The learner's choice if they made one, else the Cycle's own language — which is what the
    // session would run in anyway, so the field says so rather than "Select".
    const match = this.availableLanguages.find(([value]) => value === this.voiceSessionLanguage);
    return match ? match[1] : '';
  }

  /** Opens the same language picker as the header's translate button, reused for the settings form. */
  async openLanguageModal() {
    const current = this.voiceSessionLanguage;
    const cycle = this.voicePathLanguage;
    const modal = await this.modalController.create({
      component: TranslateComponentModalComponent,
      componentProps: {
        // Top of the list is the learner's own pick, then the Cycle's own language, then the rest
        // alphabetically — the two that are worth a glance are not somewhere down a list of
        // fifty-eight. Until they pick one, the Cycle's is the only pinned row.
        languages: this.availableLanguages.filter(([code]) => code !== current && code !== cycle),
        chosenLanguage: current && current !== cycle
          ? this.availableLanguages.find(([code]) => code === current)
          : null,
        pathLanguage: this.availableLanguages.find(([code]) => code === cycle),
        // No "None" row: blank means "reuse what is saved" to this API, never "clear", so it would
        // hand back the last language picked rather than the Cycle's. Choosing the Cycle's own
        // language — pinned above — is how that is said here.
        allowNone: false,
        // Opens on whichever language the session would run in — not on the app's translate
        // setting, which is a different choice about a different thing.
        initialLanguage: current,
        // The language the coach speaks, kept here. Saved as the app's translate language it would
        // change what community posts and comments are translated into.
        persistAsAppLanguage: false,
        title: 'Select language'
      },
      cssClass: this.isMobile ? 'pb_mobile_language_translate_modal pb_language_translate_modal' : 'pb_language_translate_modal',
      animated: false
    });
    modal.onDidDismiss().then((result) => {
      // What Done answered with. Read from the result rather than from app state: the two are the
      // same language only when the learner happens to be practising in the one the app is
      // translated into, and a cancel answers with nothing at all.
      const chosen = result?.data;
      if (!chosen) { return; }
      this.selectedLanguage = this.appState.get('selectedLanguage');
      // Into the form as well as the label: this is the language the coach will speak, and it
      // travels with the settings save and the session create. Through toVoiceLanguage because
      // the app spells three of them differently from the voice API (km/zh-CN/iw); 'none' means
      // no override, which is blank.
      this.settingsDraft = {
        ...this.settingsDraft,
        language: chosen === 'none' ? '' : toVoiceLanguage(chosen),
      };
      // The label is read during change detection, and this promise settles after the pass that
      // opened the modal — without this the button keeps showing the previous language.
      this.cdRef.detectChanges();
    });
    await modal.present();
  }

  /**
   * Every successfully completed attempt for this action, shown on the main screen as centered
   * system messages ("You practiced on [date/time]" + a link to that attempt's summary).
   * Incomplete/discarded attempts are deliberately excluded — there is nothing to link to.
   */
  /**
   * The action's history, twenty attempts at a time.
   *
   * Paged on the server rather than fetched whole: a learner with fifty attempts against one action
   * would otherwise pull all of them to render the three the screen opens on. "Show earlier history"
   * asks for the next page, and stops when the server says there are no more.
   */
  private loadLastPracticeSession() {
    this.attemptsOffset = 0;
    this.hasMoreAttempts = false;
    this.fetchAttemptPage(true);
  }

  /** One page of this action's attempts, oldest-ward from what is already held. */
  private fetchAttemptPage(isFirstPage = false, fillChasesLeft = ATTEMPT_FILL_CHASES): void {
    const screenContentId = this.screenContentId;
    if (!screenContentId || this.isLoadingAttempts) { return; }

    this.isLoadingAttempts = true;
    this.subscriptions.add(
      this.practiceBotService.getPracticeHistory(screenContentId, {
        limit: ATTEMPTS_PAGE_SIZE,
        offset: this.attemptsOffset,
      }).subscribe(
        (response: any) => {
          this.isLoadingAttempts = false;
          const [group] = this.practiceBotService.practiceActionsFrom(response);
          // Grouped under actions[] since 1 September. attemptsFrom still reads the older flat
          // shape, so a deploy where this endpoint has not rolled over yet keeps working — it
          // returns everything at once, which is one page with nothing after it.
          const page = group
            ? (group.sessions || []).map((session: any) => ({
                id: session?.practice_session_id,
                at: session?.created_at ?? session?.ended_at,
                status: session?.status,
                score: session?.performance_score ?? null,
                attemptNumber: session?.attempt_number,
                // The recording's own length where the server has it; `duration_seconds` is the
                // wall clock whenever it does not. A reported zero is not a length — the endpoint
                // accepts `>= 0` — and taken as one it blanks a row that had a real duration.
                durationSeconds: typeof session?.audio_duration_seconds === 'number'
                  && session.audio_duration_seconds > 0
                    ? session.audio_duration_seconds
                    : session?.duration_seconds,
                audioAvailable: session?.audio_available,
              }))
            : this.practiceBotService.attemptsFrom(response);

          for (const entry of page) {
            if (entry?.status === 'complete') {
              this.recordCompletedAttempt(entry.id, entry.at, entry.score ?? null, {
                attemptNumber: entry.attemptNumber,
                durationSeconds: entry.durationSeconds,
                audioAvailable: (entry as any).audioAvailable,
              });
            } else if (entry?.status === 'failed' || entry?.status === 'abandoned') {
              // Ended without a report. Still worth saying out loud — the feed reads it back as
              // "you cancelled a practice session on…" rather than letting it vanish.
              this.recordCancelledAttempt(entry.id, entry.at);
            }
            // active / analyzing are still in flight: nothing to show for them yet.
          }

          // By what the server sent, not by what was kept: statuses we do not render still occupy
          // their place in the page, and offsetting by the rendered ones would re-request them.
          this.attemptsOffset += page.length;
          this.totalAttempts = group?.sessionCount ?? page.length;
          this.hasMoreAttempts = !!group?.hasMoreSessions;
          this.cdRef.detectChanges();

          if (isFirstPage) {
            // History loads after the initial render, and can push the feed past the fold — land
            // on the newest attempt rather than leaving the learner at the top of it.
            this.scrollMainToBottom();
          }

          // A page can be all abandoned or still-analyzing attempts, none of which the report's
          // recordings list can show — it would sit empty with a "Show More" nobody knows to
          // press. Only worth chasing while that list is the thing on screen, and only a few
          // times: a page that yields nothing showable must not turn into an unbounded walk back
          // through the learner's whole history.
          const showableSoFar = this.completedAttempts.length;
          if (this.screen === 'final'
            && showableSoFar < this.RECORDINGS_STEP
            && this.hasMoreAttempts
            && page.length > 0
            && fillChasesLeft > 0) {
            this.fetchAttemptPage(false, fillChasesLeft - 1);
          }
        },
        (error) => {
          console.log('Practice history unavailable', error);
          this.isLoadingAttempts = false;
          this.cdRef.detectChanges();
        },
      )
    );
  }

  /** Adds (or refreshes) one attempt and keeps the list oldest-first, like a message feed. */
  /** A new attempt, or one removed: the History row for this action is no longer what it was. */
  private noteHistoryChanged(): void {
    this.historyChanged = true;
  }

  private recordCompletedAttempt(
    id: string,
    at: string,
    score: number | null,
    extra: { attemptNumber?: number; durationSeconds?: number; audioAvailable?: boolean } = {},
  ) {
    if (!id) { return; }
    // Keep whatever was already known about this attempt: a later page may carry the session number
    // and duration that an earlier, thinner record did not.
    const known = this.completedAttempts.find((a) => a.id === id);
    this.completedAttempts = [
      ...this.completedAttempts.filter((a) => a.id !== id),
      {
        id,
        at,
        score,
        attemptNumber: extra.attemptNumber ?? known?.attemptNumber,
        durationSeconds: extra.durationSeconds ?? known?.durationSeconds,
        audioAvailable: extra.audioAvailable ?? known?.audioAvailable,
      },
    ].sort((a, b) => new Date(a.at || 0).getTime() - new Date(b.at || 0).getTime());
    this.trackNewestAttempt();
  }

  /** The main screen's "you practiced on…" line follows the newest attempt, whichever that now is. */
  private trackNewestAttempt(): void {
    const newest = this.completedAttempts[this.completedAttempts.length - 1];
    this.lastSessionId = newest?.id;
    this.lastPracticedAt = newest?.at;
    this.lastScore = newest?.score ?? null;
  }

  /**
   * Adds an attempt that ended without a report, so the feed can say it was cancelled without
   * waiting for the history to be re-read.
   */
  private recordCancelledAttempt(id: string, at: string) {
    if (!id) { return; }
    this.cancelledAttempts = [
      ...this.cancelledAttempts.filter((attempt) => attempt.id !== id),
      { id, at, score: null },
    ];
  }

  /**
   * True when the session was too short or silent to analyse — no speech detected, or no turn
   * from the learner at all. Anything else that fails did so for a different reason.
   */
  private endedTooEarly(result: PracticeAnalysis): boolean {
    const metrics: any = (result as any)?.metrics;
    return (result?.degradations ?? []).includes('no_speech_detected')
      || metrics?.user_turns === 0;
  }

  /** Why the analysis produced nothing — `no_speech_detected` is the one worth naming. */
  private describeAnalysisFailure(result: PracticeAnalysis): string {
    const degradations = result?.degradations ?? [];
    if (degradations.includes('no_speech_detected')) {
      return this.i18n.t('We did not hear anything in that session, so there is nothing to review.');
    }
    return this.i18n.t('We could not analyse this session.');
  }

  /** The micro-action this practice belongs to, in either casing the to-do list may use. */
  get screenContentId(): number | string {
    return this.action?.screenContentId ?? this.action?.screen_content_id;
  }

  ngOnDestroy() {
    // A preview must not keep talking over a screen that has gone.
    this.stopVoiceDemo();
    this.clearFeedbackTimer();
    this.stopPlayback();
    this.stopCallTicker();
    this.detachBotAudio();
    if (this.voiceCall.isActive()) {
      this.voiceCall.stop('disconnected');
    }
    // A pre-warm still in flight when the modal closes would leave the mic live.
    this.voiceCall.releasePrewarmedMicrophone();
    this.subscriptions.unsubscribe();
    this.observeTrendBox(undefined);
    this.destroyTrendChart(true);
  }

  /** Points trendResizeObserver at `el` (or detaches it when there is no chart box). */
  private observeTrendBox(el: HTMLElement | undefined): void {
    if (el === this.trendObservedEl) {
      return;
    }
    this.trendResizeObserver?.disconnect();
    this.trendResizeObserver = undefined;
    this.trendObservedEl = el;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    // The size is part of syncTrendChart's signature, so this only rebuilds on a real change.
    this.trendResizeObserver = new ResizeObserver(() => this.syncTrendChart());
    this.trendResizeObserver.observe(el);
  }

  /**
   * Keeps the trend line's canvas in sync with the current attempt's scores.
   *
   * The canvas mounts and unmounts with `*ngIf="trend.length"`, so there is no single lifecycle
   * moment to hook a redraw off of — ngAfterViewChecked runs after every check, and the signature
   * guard turns that into a no-op except on the tick where the canvas or the trend actually changed.
   */
  ngAfterViewChecked(): void {
    this.syncTrendChart();
  }

  /**
   * Destroys the trend chart and, when its canvas is going away for good, frees the canvas's
   * backing store right away. iOS caps total canvas memory per page much lower than the simulator
   * or desktop do, and a detached canvas holds its (devicePixelRatio-scaled) bitmap until GC.
   */
  private destroyTrendChart(releaseCanvas: boolean): void {
    const canvas: HTMLCanvasElement | undefined = (this.trendChartInstance as any)?.chart?.canvas;
    this.trendChartInstance?.destroy();
    this.trendChartInstance = undefined;
    if (releaseCanvas && canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }

  private syncTrendChart(): void {
    const canvas = this.trendCanvasRef?.nativeElement;
    const scrollInner = this.trendScrollRef?.nativeElement;
    this.observeTrendBox(scrollInner);
    if (!canvas || !scrollInner) {
      this.destroyTrendChart(true);
      this.trendChartSignature = '';
      return;
    }

    const trend = this.trend;
    // Each point's own session number, same lookup as the recordings list (`recordingLabel`) —
    // falling back to its place in the trend only until that attempt's number is known.
    const labels = trend.map((entry, index) => {
      const known = this.completedAttempts.find((attempt) => attempt.id === entry?.practice_session_id);
      return `S${known?.attemptNumber ?? index + 1}`;
    });
    const scores = trend.map((entry) => Math.max(0, Math.min(100, entry?.performance_score ?? 0)));

    // Widens the scroll box past the card once there isn't room for every point at a readable
    // spacing, rather than squeezing them all in — .pb-chart scrolls horizontally for the rest.
    // Sized from the point count alone; .pb-chart__scroll's `min-width: 100%` (SCSS) is what keeps
    // a short list filling the box instead of leaving it stranded at some arbitrary smaller width.
    // Kept equal to pbXAxisMinTickSpacingPx below — one is what forces the room to exist, the
    // other is what stops labels being drawn into a gap narrower than that.
    const pointSpacingPx = 50;

    const contentWidth = scores.length > 1 ? (scores.length - 1) * pointSpacingPx + 70 : 0;
    scrollInner.style.width = contentWidth ? `${contentWidth}px` : '';

    // The canvas's laid-out box (the SCSS stretches it to fill .pb-chart__scroll). Measured here
    // rather than left to Chart.js's `responsive` mode: that re-measures only when its hidden
    // iframe fires `onresize`, which on iOS devices it doesn't reliably do — a chart built while
    // the box was still 0×0 (modal not yet presented / mid-layout) stayed blank. Making the size
    // part of the signature means any check that sees the real box rebuilds at the right size.
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Keyed off the resolved labels rather than the raw trend, so a redraw still fires once
    // completedAttempts (loaded separately) fills in a session number the first pass fell back on.
    const signature = JSON.stringify({ labels, scores, width, height });
    if (signature === this.trendChartSignature && this.trendChartInstance) {
      return;
    }
    this.trendChartSignature = signature;
    const lineColor = getComputedStyle(document.documentElement).getPropertyValue('--theme-color').trim() || '#6C8CFF';

    // Fixed 0–100 axis with ticks at 25, 50, 75 and 100, regardless of the scores plotted.
    const yStep = 25;
    const yMax = 100;

    this.destroyTrendChart(false);
    // With `responsive: false`, Chart.js takes its CSS size from these attributes and scales the
    // backing store by devicePixelRatio itself (helpers.retinaScale).
    canvas.width = width;
    canvas.height = height;
    this.trendChartInstance = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data: scores,
          borderColor: lineColor,
          backgroundColor: 'transparent',
          pointBackgroundColor: lineColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 2.5,
          pointRadius: 5,
          pointHoverRadius: 6,
          borderWidth: 3,
          lineTension: .25
        }]
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        animation: false,
        legend: { display: false },
        title: { display: false },
        tooltips: { enabled: false },
        hover: { mode: null },
        pbShowValueLabels: true,
        pbXAxisMinTickSpacingPx: pointSpacingPx,
        pbXAxisRightMarginPx: 10,
        pbYAxisTopPaddingPx: 20,
        scales: {
          yAxes: [{
            ticks: {
              min: 0, max: yMax, stepSize: yStep,
              fontColor: '#A4A7AE', fontFamily: 'Inter', fontSize: 11,
              padding: 20
            },
            gridLines: {
              color: 'rgba(164, 167, 174, .35)',
              borderDash: [4, 4],
              zeroLineColor: 'rgba(164, 167, 174, .45)',
              drawBorder: false
            }
          }],
          xAxes: [{
            ticks: {
              fontColor: '#A4A7AE', fontFamily: 'Inter', fontSize: 11, fontWeight: 600,
              autoSkip: false
            },
            gridLines: { display: false }
          }]
        }
      }
    });
  }

  showScreen(screen: PracticeBotScreen) {
    // Keyboard room belongs to the screen that asked for it. Left behind, a missed keyboardWillHide
    // would strand a gap the size of the keyboard on whatever screen came next.
    this.keyboardOffset = 0;
    this.keyboardShowing = false;
    // Remember where the report was opened from, so Back returns there. A live call, the
    // "preparing your feedback" wait and the report itself are places to come back *from*,
    // never to — going back into them would restart a call or an auto-advancing timer.
    if (BACKABLE_SCREENS.includes(this.screen) && !BACKABLE_SCREENS.includes(screen)) {
      this.returnScreen = this.screen;
    }

    // Any pending auto-advance belongs to the screen we're leaving.
    this.clearFeedbackTimer();
    // So does a voice preview: the picker is only on the settings screen, and a clip left running
    // carried on over the preview screen — or over the first words of a live call.
    if (this.screen === 'settings' && screen !== 'settings') {
      this.stopVoiceDemo();
    }
    this.screen = screen;
    // this.showScene = false;

    // Only fall back to the timed walkthrough when no real analysis is in flight.
    if (screen === 'feedback' && !this.isAnalyzing) {
      this.feedbackTimer = setTimeout(() => this.showScreen('final'), FEEDBACK_LOADING_MS);
    }

    if (screen === 'settings') {
      // Edits start from what is saved, and are thrown away unless Save commits them.
      this.settingsDraft = { ...this.settings };
      // ngOnInit's fetch can have failed — give the form another chance at its starting values
      // rather than opening it empty.
      if (!this.defaultsLoaded) { this.loadPracticeDefaults(); }
    }

    // Arriving on the preview fresh — from Save, from a call that never started, from a report
    // — should show the newest line, not wherever the feed last happened to be scrolled.
    if (screen === 'main') {
      this.scrollMainToBottom();
    }

    if (screen !== 'final') {
      // Moved on from the report — Back belongs to this modal from here on.
      this.openedOnSummary = false;
    }

    if (screen === 'final') {
      // Presigned and short-lived, so it is fetched when the report opens, not before.
      this.loadPlaybackUrl();
      // The recordings list is open from the start, so its first page is fetched with the report
      // rather than on a tap. Guarded on the offset so re-entering the screen — or arriving from a
      // main screen that has already paged — does not fetch again.
      if (!this.attemptsOffset) { this.loadMoreRecordings(); }
    } else {
      // Leaving the report must not leave audio playing underneath another screen.
      this.stopPlayback();
      this.stopRowPlayback();
    }
  }

  /**
   * The report's Back button. Opened from the coach's "View Feedback", the screen behind it is
   * the coach itself, so Back closes the modal rather than stepping into a preview the learner
   * never came from.
   */
  goBack() {
    if (this.openedOnSummary) {
      this.close();
      return;
    }
    this.showScreen(this.returnScreen);
  }

  private clearFeedbackTimer() {
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = undefined;
    }
  }

  // toggleScene() {
  //   this.showScene = !this.showScene;
  // }

  /** Step 1 — mint the session, then show the voice screen with its scenario. */
  /**
   * Settle the microphone BEFORE the session exists.
   *
   * Creating it first meant a learner who refused (or had the mic blocked in Settings) still spent a
   * POST /practice/session — one of ten allowed in an hour — which then had to be deleted again as an
   * orphan. Nothing about a practice session is useful without a microphone, so this is the first
   * question to ask, not the last.
   */
    private async ensureMicrophoneReady(): Promise<boolean> {
    try {
      // Android's runtime grant first, then getUserMedia — the order Android requires. On iOS and
      // the browser the first call is a no-op and the second is the only gate.
      await this.ensureMicrophonePermission();
      await this.voiceCall.microphoneReady();
      return true;
    } catch (error) {
      // The native permission dialog paused the app; dismissing it fires a `resume` a moment
      // later. Keep clearStaleErrorOnResume from treating THAT resume as a return-from-Settings
      // and wiping the error we're about to show.
      this.micPromptResumeGraceUntil =
        Date.now() + PracticeBotModalComponent.MIC_PROMPT_RESUME_GRACE_MS;
      this.voiceCall.releasePrewarmedMicrophone();
      this.isStarting = false;
      this.showError(this.describeCallError(error));
      this.cdRef.detectChanges();
      return false;
    }
  }

  async startVoicePractice(): Promise<void> {
    const screenContentId = this.action?.screenContentId ?? this.action?.screen_content_id;
    if (!screenContentId || this.isStarting) {
      // Nothing to practise against (or already starting) — keep the existing walkthrough behaviour.
      this.showScreen('voice');
      return;
    }

    this.isStarting = true;
    this.errorMessage = '';
    // The microphone needs nothing from the session, so acquire it WHILE the session is being minted
    // rather than after. Serialising the two put the whole cost of getUserMedia (and, the first time,
    // the permission prompt) in front of the coach's first word.
    //
    // No microphone, no session: this both asks in the right order on every platform and keeps a
    // refusal from costing a session.
    if (!(await this.ensureMicrophoneReady())) { return; }
    this.subscriptions.add(
      this.practiceBotService
        // The settings the preview screen just showed, so the call runs THAT scenario rather than a
        // freshly generated one — and skips the generation step entirely (see createPracticeSession).
        .createPracticeSession({
          screenContentId,
          customContext: this.customContext,
          settings: this.settings,
          // The gender/tone the learner chose, resolved to one of the ids `defaults` offered.
          // Without this their voice choice never left the settings screen.
          voiceId: this.practiceBotService.resolveVoiceId(
            this.availableVoices, this.settings?.voice_gender, this.settings?.voice_tone
          ),
          // Blank reuses what the server has saved for this action, or the learner's last choice.
          language: this.settings?.language,
          accent: this.settings?.accent,
        })
        .subscribe(
          (response) => {
            this.isStarting = false;
            // Logged so a payload whose persona/scenario fields differ from the spec is obvious
            // rather than silently degrading every label to "Coach".
            console.log('[practice] session scenario:', response?.scenario);
            this.practiceSessionId = response?.practice_session_id;
            // A new attempt must not inherit the last one's status.
            this.practiceStatus = '';
            this.setPracticeStatus(response);
            this.scenario = response?.scenario;
            // What the call is ACTUALLY running with, after the server resolved request → this
            // action's saved value → the learner's last choice → the Cycle's language. Taken back
            // so the settings screen shows what is active rather than what was asked for — and
            // because creating a session is itself a save.
            this.applyResolvedVoiceSettings(response?.scenario);
            this.maxDurationSeconds = response?.max_duration_seconds || 600;
            this.transcript = [];
            // A fresh attempt must always be endable, whatever the previous one left behind.
            this.isEnding = false;
            this.isDiscarded = false;
            this.showNoiseHint = false;
            // The timer label reads this field, and the voice screen renders before start() has
            // finished acquiring the mic and exchanging SDP — so without this the previous attempt's
            // duration is on screen for those seconds. Only endAndDiscard() used to clear it, which
            // left every other ending (timeout, bot_ended, hang-up) showing a stale clock.
            this.callElapsedMs = 0;
            mixpanel.track('practice session started', {
              screenContentId,
              practiceSessionId: this.practiceSessionId,
              hasCustomContext: !!this.customContext,
            });
            this.showScreen('voice');
            this.cdRef.detectChanges();
            this.openVoiceCall(response);   // step 2 — WebRTC
          },
          (error) => {
            this.isStarting = false;
            this.voiceCall.releasePrewarmedMicrophone();   // no call will use it
            this.showError(this.describeCreateError(error));
            mixpanel.track('practice session start failed', { screenContentId, status: error?.status });
            this.cdRef.detectChanges();
          }
        )
    );
  }

  /** Step 2 — open the WebRTC call with the secret + model minted in step 1. */
  private async openVoiceCall(session: any) {
    try {
      // Android holds the mic behind a runtime grant even with RECORD_AUDIO in the manifest, and
      // the WebView refuses getUserMedia until it is held.
      await this.ensureMicrophonePermission();
      await this.voiceCall.start(session);
      this.isMuted = false;
      this.startCallTicker();
    } catch (error) {
      console.log('[practice] voice call failed to start', {
        name: (error as any)?.name,
        message: (error as any)?.message,
        hasMediaDevices: !!navigator?.mediaDevices?.getUserMedia,
        error,
      });
      const orphanedId = this.practiceSessionId;
      this.practiceSessionId = '';
      if (orphanedId) {
        this.subscriptions.add(
          this.practiceBotService.deletePracticeSession(orphanedId).subscribe(
            () => {},
            (deleteError) => console.log('Practice orphan cleanup failed', deleteError)
          )
        );
      }
      this.showError(this.describeCallError(error));
      mixpanel.track('practice voice call failed', {
        practiceSessionId: orphanedId,
        reason: (error as any)?.name || 'unknown',
      });
      this.showScreen('main');
      this.cdRef.detectChanges();
    }
  }

  /**
   * Asks Android for the microphone before the call needs it. A refusal surfaces as the same
   * NotAllowedError the browser raises, so the wording stays in one place.
   */
  private async ensureMicrophonePermission(): Promise<void> {
    if (!this.platform.is('cordova') || !this.platform.is('android')) {
      console.log('[practice] mic: not cordova/android, leaving it to the browser');
      return;
    }

    try {
      // Inside the try: reading PERMISSION throws if the plugin has not attached to the
      // WebView yet, and that must not look like a refusal.
      const mic = this.androidPermissions.PERMISSION.RECORD_AUDIO;
      const held = await this.androidPermissions.checkPermission(mic);
      console.log('[practice] mic: already held?', held?.hasPermission);
      if (held?.hasPermission) { return; }

      const granted = await this.androidPermissions.requestPermission(mic);
      console.log('[practice] mic: granted after asking?', granted?.hasPermission);
      if (granted?.hasPermission) { return; }
    } catch (error) {
      // Plugin missing or misbehaving — let getUserMedia have its own go, which is what the
      // WebView would do anyway.
      console.log('[practice] mic: permission check failed, falling through', error);
      return;
    }
    // Asked and refused. Android also reports a permission missing from the manifest this way,
    // without ever showing a dialog.
    throw { name: 'NotAllowedError' };
  }

  /** Mic permission is the common failure here, and it needs its own wording. */
  private describeCallError(error: any): string {
    const name = error?.name;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      // Deliberately covers BOTH refusals. iOS asks twice — once as the app (Settings) and again as
      // the WebView, in its own prompt — and getUserMedia reports the two identically, so naming
      // Settings alone told a learner who had just declined the in-app prompt to go and change a
      // setting that was already correct.
      return this.i18n.t('Microphone access was denied. Allow the microphone when asked, and check it is enabled for this app in Settings.');
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return this.i18n.t('No microphone found. Connect one and try again.');
    }
    // No mic API at all: an insecure origin, or a WebView without WebRTC. "Check your
    // connection" would send the learner looking in the wrong place.
    if (!navigator?.mediaDevices?.getUserMedia) {
      return this.i18n.t('This app build cannot record audio. Please update to the latest version.');
    }
    return this.i18n.t('We could not connect the voice call. Please check your connection and try again.');
  }

  /**
   * The bot becomes audible once the remote track is attached. play() is called here rather than left
   * to `autoplay`, which on iOS claims the audio session before the mic has put it into record mode.
   */
  private attachBotAudio(stream: MediaStream) {
    const element = this.botAudio?.nativeElement;
    if (!element) { return; }
    // Re-assigning srcObject resets the element's playout and drops whatever is buffered, cutting the
    // coach off mid-sentence. The stream object is stable, so only attach it the first time.
    if (element.srcObject !== stream) { element.srcObject = stream; }
    this.playBotAudio(element);
  }

  /**
   * iOS only starts playback from a user gesture, and the remote track lands long after the tap that
   * began the call. If it refuses, retry on the next touch rather than staying silent all session.
   */
  private playBotAudio(element: HTMLAudioElement) {
    element.play?.()?.catch?.((error) => {
      console.log('Practice bot audio playback blocked, will retry on the next touch', error);
      this.retryBotAudioOnGesture();
    });
  }

  private retryBotAudioOnGesture() {
    if (this.botAudioRetry) { return; }
    this.botAudioRetry = () => {
      const element = this.botAudio?.nativeElement;
      this.clearBotAudioRetry();          // one shot; playBotAudio re-arms it if it fails again
      if (element) { this.playBotAudio(element); }
    };
    document.addEventListener('touchend', this.botAudioRetry, true);
    document.addEventListener('click', this.botAudioRetry, true);
  }

  private clearBotAudioRetry() {
    if (!this.botAudioRetry) { return; }
    document.removeEventListener('touchend', this.botAudioRetry, true);
    document.removeEventListener('click', this.botAudioRetry, true);
    this.botAudioRetry = undefined;
  }

  /** Releases the sink. It must let go of the stream BEFORE the tracks and peer connection close. */
  private detachBotAudio() {
    this.clearBotAudioRetry();
    const element = this.botAudio?.nativeElement;
    if (!element) { return; }
    try {
      element.pause();
      element.srcObject = null;
    } catch (e) {
      // The element went with the screen already — nothing left to release.
    }
  }

  /**
   * Keyboard handling, ported from micro-action-chatbot-v1 so both screens behave the same way.
   *
   * iOS needs it because config.xml sets `KeyboardResize: false`: the keyboard is drawn OVER the
   * WebView without resizing it, so the page never learns it has less room. Nothing here is a
   * per-device constant — the height comes from the platform and the space already below the modal
   * is measured, which is what makes it hold on any screen size (and in the web modal, which has
   * margins the full-screen one does not).
   */
  /**
   * A microphone error describes the state at the moment of the attempt, and nothing re-checks it.
   * The learner who reads "allow the microphone in app settings", goes and does exactly that, and
   * comes back, would otherwise return to the same message still on screen — looking as though the
   * grant had not worked. Clear it on resume and let the next attempt speak for itself.
   */
    private clearStaleErrorOnResume() {
    const resume: any = (this.platform as any)?.resume;
    if (!resume?.subscribe) { return; }
    this.subscriptions.add(resume.subscribe(() => {
      if (!this.errorMessage) { return; }
      // A resume right after we showed the permission dialog is that dialog closing, not the
      // learner coming back from Settings — leave the error we just set alone.
      if (Date.now() < this.micPromptResumeGraceUntil) { return; }
      this.errorMessage = '';
      this.cdRef.detectChanges();
    }));
  }

  private registerKeyboardEvents() {
    // iOS device and browser only.
    //
    // iOS: config.xml sets `KeyboardResize: false` under <platform name="ios">, so the keyboard is
    // drawn over the WebView and NOTHING moves the focused field — we have to make the room.
    // Android: scripts/remove_permissions.js sets windowSoftInputMode to `adjustPan`, so the OS pans
    // the window to keep the focused field visible by itself. Adding our own offset on top would be
    // a second, competing correction.
    if (this.isNativeAndroid) { return; }
    if (this.platform.is('cordova')) {
      this.isCordovaMobile = true;
      this.subscriptions.add(
        this.keyboard.onKeyboardWillShow().subscribe((info: any) =>
          this.moveInputsUp(info?.keyboardHeight ?? 0))
      );
      this.subscriptions.add(
        this.keyboard.onKeyboardWillHide().subscribe(() => this.resetInputs())
      );
      return;
    }
    const viewport: any = (window as any).visualViewport;
    if (!viewport) { return; }
    this.subscriptions.add(
      fromEvent(viewport, 'resize').subscribe(() => {
        const height = window.innerHeight - (viewport.height ?? window.innerHeight);
        // 150 filters out browser chrome shifting, which is not a keyboard.
        if (height > 150) { this.moveInputsUp(height); }
        else if (this.keyboardShowing) { this.resetInputs(); }
      })
    );
  }

  private moveInputsUp(keyboardHeight: number) {
    if (this.keyboardShowing) { return; }
    // Only the screens that have a text field; moving the others is what caused overlay flashes
    // elsewhere in the app.
    if (this.screen !== 'settings' && this.screen !== 'main') { return; }
    // iOS fires onKeyboardWillShow (and the browser can fire a viewport resize) whenever ANY
    // focusable control becomes active, radios and checkboxes included — not only text fields.
    // The voice-tone pb-option radios are exactly that: tapping one focuses a real (visually
    // hidden) <input type="radio">, with no keyboard ever opening, which was padding the form
    // by a phantom keyboard height and leaving a gap above Save/Reset.
    if (!this.isTextEntryFocused()) { return; }
    this.keyboardShowing = true;
    const host = this.elRef?.nativeElement as HTMLElement;
    // Whatever room already exists under the modal does not need making twice.
    const spaceBelow = host
      ? window.innerHeight - host.getBoundingClientRect().bottom
      : 0;
    this.keyboardOffset = Math.max(0, keyboardHeight - spaceBelow - 1);
    this.cdRef.detectChanges();
    // Once the room is in the DOM there is somewhere to scroll the field to.
    setTimeout(() => (document.activeElement as HTMLElement)?.scrollIntoView?.({
      block: 'center', behavior: 'smooth',
    }), 50);
  }

  /**
   * A tap on a pb-option label would otherwise let the browser forward its default click to the
   * (visually hidden) radio, focusing it — and on iOS, focusing ANY form control drags the whole
   * screen up to "reveal" it, even one that never opens a keyboard. Handling the tap here keeps
   * `settingsDraft.voice_tone` in sync without that jump: focus still lands on the real input
   * (so tab/arrow-key navigation from here on works exactly as before), just without the scroll.
   * Keyboard-driven selection is untouched — it never goes through this handler, only (change).
   */
  /** Stable identity for the tone rows, so a rebuild reuses them. */
  trackVoiceTone(_index: number, tone: PracticeVoiceToneOption) {
    return tone?.value;
  }

  selectVoiceTone(value: string, event: MouseEvent) {
    // Selecting a radio with the arrow keys raises a click of its own, with no pointer behind it
    // (`detail` 0). Cancelling THAT is what left the tick on the old row while the new voice
    // played: preventDefault undoes the browser's own selection, and it does so after this handler
    // has run, so the model moved and the DOM snapped back. Keyboard selection is left to the
    // browser, and `(change)` picks it up.
    if (!event?.detail) { return; }
    event.preventDefault();
    this.settingsDraft.voice_tone = value;
    const input = (event.currentTarget as HTMLElement)?.querySelector('input');
    input?.focus?.({ preventScroll: true });
    // Choosing a voice plays it, once — the point of the list is to hear the difference, and a
    // separate play control would make that a second decision.
    this.previewVoiceTone(value);
  }

  /**
   * The same thing for a voice chosen without a tap.
   *
   * Arrow keys and space move a radio group's selection and raise `change` without ever raising a
   * click, so `selectVoiceTone` above never sees them — walking the list from the keyboard used to
   * switch voices in silence. Every way of choosing a voice plays it.
   *
   * A tap does not come through here (the label handler calls preventDefault, so the click never
   * reaches the input); if a browser forwarded one anyway, the preview's own guard keeps it from
   * playing the same clip twice.
   */
  onVoiceToneChanged(value: string, event?: Event) {
    if (!value) { return; }
    this.settingsDraft.voice_tone = value;
    this.previewVoiceTone(value);
    // The list scrolls at about four rows, and the browser only brings the focused element into
    // view — which here is a 1px, clipped input sitting at the top of a 51px row, so the row it
    // belongs to stayed half off the list. Scroll the row itself, and only as far as it needs.
    const row = (event?.target as HTMLElement)?.closest?.('.pb-option') as HTMLElement | null;
    row?.scrollIntoView?.({ block: 'nearest' });
  }

  /**
   * Plays the pre-generated clip for one tone, in the language the settings are set to.
   *
   * Tapping a row while another is playing stops that one and plays this one from the start — one
   * clip at a time. Tapping the row that is ALREADY being heard is ignored: a clip plays once per
   * choice, and restarting it under the learner's fingers is not something anyone asked for. Once
   * it has finished, tapping the same row plays it again — that is a fresh choice to hear it.
   *
   * The URL is presigned for about five days and the server hands the same one to everyone, so it
   * is worth holding for this run — and the browser can then serve the audio from its own cache on
   * a second listen.
   */
  previewVoiceTone(toneValue: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!toneValue) { return; }
    // Already being heard — or already on its way. A second tap on it is not a new choice, and
    // starting over mid-sentence is what "resets to the start" looked like.
    if (this.playingToneValue === toneValue || this.loadingToneValue === toneValue) { return; }

    // Whatever was playing stops: one clip at a time. This also retires every earlier request.
    this.stopVoiceDemo();
    const request = this.demoRequestId;

    const language = toVoiceLanguage(this.settingsDraft?.language) || this.voicePathLanguage || 'en';
    const key = `${toneValue}|${language}`;
    const cached = this.demoUrls.get(key);
    if (cached) { this.startVoiceDemo(toneValue, cached, request); return; }

    this.loadingToneValue = toneValue;
    this.subscriptions.add(
      this.practiceBotService.getVoiceDemoUrl(toneValue, language).subscribe(
        (response) => {
          const url = response?.url || '';
          // The learner tapped something else while this was in the air. The URL is still worth
          // keeping — the next tap on this voice plays it without a round trip — but starting it
          // now would talk over the voice they actually chose.
          if (url) { this.demoUrls.set(key, url); }
          if (request !== this.demoRequestId) { return; }
          this.loadingToneValue = '';
          if (!url) { this.showVoiceDemoUnavailable(); return; }
          this.startVoiceDemo(toneValue, url, request);
        },
        () => {
          // Same again: a failure for a voice they have already moved off is not worth a toast.
          if (request !== this.demoRequestId) { return; }
          this.loadingToneValue = '';
          this.showVoiceDemoUnavailable();
          this.cdRef.detectChanges();
        },
      )
    );
  }

  /** Stops whatever preview is playing — on leaving the screen, or before starting another. */
  stopVoiceDemo(): void {
    if (this.demoAudio) {
      // Handlers off FIRST. Clearing src fires the element's own error event ("empty src"), which
      // went straight to onerror — so finishing a clip normally raised "no preview for this voice".
      this.demoAudio.onplaying = null;
      this.demoAudio.onended = null;
      this.demoAudio.onerror = null;
      this.demoAudio.ontimeupdate = null;
      this.demoAudio.pause();
      this.demoAudio.removeAttribute('src');
      this.demoAudio = null;
    }
    this.playingToneValue = '';
    this.loadingToneValue = '';
    // Anything still in flight belongs to a tap the learner has moved past.
    this.demoRequestId++;
  }

  private startVoiceDemo(toneValue: string, url: string, request: number): void {
    const audio = new Audio(url);
    this.demoAudio = audio;
    this.loadingToneValue = toneValue;
    audio.onplaying = () => {
      this.loadingToneValue = '';
      this.playingToneValue = toneValue;
      this.cdRef.detectChanges();
    };
    audio.onended = () => { this.stopVoiceDemo(); this.cdRef.detectChanges(); };
    // The generated clips carry a little silence after the last word, and `ended` reports the end
    // of the FILE — so the bars kept waving over nothing. They settle once the audio is within a
    // breath of finishing; the element plays out the tail and `ended` still does the cleanup.
    audio.ontimeupdate = () => {
      // Not `duration || 0`: an unknown duration is NaN, which is falsy, and 0 reads as "at the
      // end" — the bars would settle the moment playback began.
      const duration = audio.duration;
      if (!isFinite(duration) || duration <= 0) { return; }
      const remaining = duration - (audio.currentTime || 0);
      if (remaining > DEMO_TAIL_SECONDS) { return; }
      if (!this.playingToneValue) { return; }
      this.playingToneValue = '';
      this.cdRef.detectChanges();
    };
    // The signed URL comes back whether or not that tone/language pair was ever generated, so a
    // missing clip shows up here as S3's own 404 rather than as a failed API call.
    audio.onerror = () => {
      this.demoUrls.delete(`${toneValue}|${toVoiceLanguage(this.settingsDraft?.language) || this.voicePathLanguage || 'en'}`);
      this.stopVoiceDemo();
      this.showVoiceDemoUnavailable();
      this.cdRef.detectChanges();
    };
    audio.play().catch(() => {
      // Tearing down a clip before it starts rejects its own play() with AbortError — so tapping
      // through the list quickly used to stop the clip that had just started and warn about a
      // voice that was perfectly fine. Only the current tap may report anything.
      if (request !== this.demoRequestId) { return; }
      this.stopVoiceDemo();
      this.showVoiceDemoUnavailable();
      this.cdRef.detectChanges();
    });
  }

  private showVoiceDemoUnavailable(): void {
    this.toasterService.warning(this.i18n.t('No preview for this voice in the selected language.'));
  }

  /**
   * Only text inputs and textareas open a real keyboard — radios and checkboxes never do.
   * `<ion-textarea>` (the "Customize scenario" box on the main screen) shadow-hosts its native
   * textarea, so `document.activeElement` reports the ion-textarea element itself rather than
   * a TEXTAREA tag — that host is trusted here rather than crawled into its shadow root.
   */
  private isTextEntryFocused(): boolean {
    const active = document.activeElement as HTMLInputElement | null;
    if (!active) { return false; }
    if (active.tagName === 'TEXTAREA' || active.tagName === 'ION-TEXTAREA' || active.tagName === 'ION-INPUT') {
      return true;
    }
    if (active.tagName === 'INPUT') {
      const type = (active.type || 'text').toLowerCase();
      return !['radio', 'checkbox', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
    }
    return false;
  }

  private resetInputs() {
    // Enable the transition BEFORE the padding changes so the dismiss animates. Every other padding
    // change stays instant (transition: none on the base class), which is what keeps the show path
    // travelling with the keyboard instead of lagging behind it.
    this.keyboardDismissing = true;
    this.keyboardShowing = false;
    this.keyboardOffset = 0;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.keyboardDismissing = false;
      this.cdRef.detectChanges();
    }, 300);
  }

  private startCallTicker() {
    this.stopCallTicker();
    // Take the first reading immediately: the interval's first tick is a second away, and a second
    // of the wrong number is exactly what the learner notices.
    this.callElapsedMs = this.voiceCall.elapsedMs;
    this.callTicker = setInterval(() => {
      this.callElapsedMs = this.voiceCall.elapsedMs;
      this.cdRef.detectChanges();
    }, 1000);
  }

  private stopCallTicker() {
    if (this.callTicker) {
      clearInterval(this.callTicker);
      this.callTicker = undefined;
    }
  }

  /** mm:ss / mm:ss for the voice screen timer. */
  get callTimerLabel(): string {
    if (!this.practiceSessionId) { return ''; }
    return `${this.formatDuration(this.callElapsedMs / 1000)} / ${this.formatDuration(this.maxDurationSeconds)}`;
  }

  /**
   * mm:ss. Minutes are padded for the call timer, which reads as a clock and must not jump width
   * as it ticks past ten minutes; the recordings list wants them bare, as "4:01" rather than "04:01".
   */
  private formatDuration(totalSeconds: number, { padMinutes = true }: { padMinutes?: boolean } = {}): string {
    const safe = Math.max(0, Math.floor(totalSeconds || 0));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    const shownMinutes = padMinutes ? String(minutes).padStart(2, '0') : String(minutes);
    return `${shownMinutes}:${String(seconds).padStart(2, '0')}`;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.voiceCall.setMuted(this.isMuted);
  }

  // ── Live transcript (call screen) ───────────────────────────────────────────

  /**
   * The AI coach's name, as minted with this session. Tolerates the shapes the payload may use so a
   * naming difference doesn't silently degrade every label to "Coach".
   */
  get personaName(): string {
    const scenario: any = this.scenario;
    return scenario?.persona_name
      || scenario?.personaName
      || scenario?.persona?.name
      || scenario?.persona
      || '';
  }

  /** Speaker label for a turn (completed or in-progress): the persona for the bot, "You" for the learner. */
  speakerLabel(turn: { role?: string }): string {
    if (turn?.role === 'assistant') { return this.personaName || 'Coach'; }
    return 'You';
  }

  /** Stable identity for *ngFor so existing lines are not re-rendered on every new turn. */
  trackTurn(index: number, turn: PracticeTranscriptTurn) {
    return `${index}-${turn?.started_at_ms}`;
  }

  /** Bound to the voice screen's ion-content: a finger down suspends auto-scroll until it lifts. */
  onVoiceContentTouchStart() {
    this.isVoiceContentTouchActive = true;
  }

  onVoiceContentTouchEnd() {
    this.isVoiceContentTouchActive = false;
  }

  /** Distance (px) from the bottom that still counts as "following along" rather than a deliberate scroll up. */
  private static readonly AUTOSCROLL_SLACK_PX = 48;

  private isFollowingTranscript(): boolean {
    if (this.isVoiceContentTouchActive) { return false; }
    if (!this.transcriptScroller && this.voiceContent?.getScrollElement) {
      this.voiceContent.getScrollElement().then((el) => (this.transcriptScroller = el));
      return true;
    }
    const scroller = this.transcriptScroller || this.resolveScroller(this.transcriptLog?.nativeElement);
    if (!scroller) { return true; }
    const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    return distanceFromBottom <= PracticeBotModalComponent.AUTOSCROLL_SLACK_PX;
  }

  /** Pin the transcript to the newest line as turns stream in. */
  private scrollTranscriptToBottom() {
    setTimeout(() => {
      // The voice screen is an <ion-content>, whose scrolling element lives in its SHADOW DOM
      // (::part(scroll)). That is why setting scrollTop on the transcript div did nothing and why
      // walking parentElement never finds a scroller — the boundary hides it. Ionic's own API is
      // the only reliable handle, so use it first.
      if (this.voiceContent?.scrollToBottom) {
        this.voiceContent.scrollToBottom(0);
        return;
      }

      const element = this.transcriptLog?.nativeElement;
      if (!element) { return; }
      // Fallbacks for a non-ion host (and for tests): a real scrolling ancestor, else let the
      // browser resolve the scroller by bringing the newest line into view.
      const scroller = this.resolveScroller(element);
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
        return;
      }
      // 'nearest' scrolls the minimum needed — it will not yank the whole modal to the bottom.
      element.lastElementChild?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  /** Nearest ancestor (including the log itself) that actually scrolls, or null if none does. */
  private resolveScroller(element: HTMLElement): HTMLElement {
    let node: HTMLElement = element;
    while (node) {
      if (node.scrollHeight > node.clientHeight + 1) { return node; }
      node = node.parentElement as HTMLElement;
    }
    return null;
  }

  /** Overshoot past the true bottom, so the last line clears the input pill instead of sitting flush under it. */
  private static readonly MAIN_SCROLL_OVERSHOOT_PX = 25;
  /**
   * Retry delays (ms) for pinning the preview to the bottom. A single attempt can land short of
   * the true bottom when something — a late reflow, an Ionic component still settling in, the
   * next chunk of history landing a moment after this one — grows the content right after that
   * attempt measured it. Retrying over this window re-measures every time and always converges
   * on wherever the real bottom ends up.
   */
  private static readonly MAIN_SCROLL_RETRY_DELAYS_MS = [0, 60, 200, 450];

  /** Pin the preview screen to the newest line as the conversation and the attempt feed grow. */
  private scrollMainToBottom() {
    PracticeBotModalComponent.MAIN_SCROLL_RETRY_DELAYS_MS.forEach((delay) => {
      setTimeout(() => this.performMainScrollToBottom(), delay);
    });
  }

  private performMainScrollToBottom() {
    // This content sits in plain flow, not a dedicated scroll region, so more than one
    // ancestor can genuinely be overflowing at once (e.g. ion-content's own box AND something
    // wrapping it). Scrolling only the nearest one left whatever the outer one still owned
    // stranded below the fold — so every overflowing ancestor gets scrolled, not just the first.
    const scrolledAny = this.scrollEveryOverflowingAncestor(this.mainTimeline?.nativeElement);

    if (this.mainContent?.getScrollElement) {
      // Ionic's own scrollToBottom() lands exactly on scrollHeight, with no way to overshoot
      // it — read the real scroller instead so the extra px below applies here too.
      this.mainContent.getScrollElement().then((el) => {
        el.scrollTo({
          top: el.scrollHeight + PracticeBotModalComponent.MAIN_SCROLL_OVERSHOOT_PX,
          behavior: 'smooth',
        });
      });
      return;
    }
    if (scrolledAny) { return; }
    this.mainTimeline?.nativeElement?.lastElementChild
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /** Walks up from `element`, scrolling every ancestor that actually overflows to its bottom. */
  private scrollEveryOverflowingAncestor(element: HTMLElement): boolean {
    let node: HTMLElement = element;
    let scrolledAny = false;
    while (node) {
      if (node.scrollHeight > node.clientHeight + 1) {
        const target = node.scrollHeight + PracticeBotModalComponent.MAIN_SCROLL_OVERSHOOT_PX;
        if (typeof node.scrollTo === 'function') {
          node.scrollTo({ top: target, behavior: 'smooth' });
        } else {
          node.scrollTop = target;
        }
        scrolledAny = true;
      }
      // The document itself scrolling is the browser window, not this modal — stop there.
      node = (node === document.body ? null : node.parentElement) as HTMLElement;
    }
    return scrolledAny;
  }

  /**
   * Closes the live call and hands its recordings + transcript to step 3. Used by the End button
   * and by the call ending itself (bot_ended / timeout / disconnected).
   */
  private async finishCall(endedReason: PracticeEndedReason) {
    // Already ending, or this session has been ended once already — don't upload/end twice.
    // A discarded attempt has nothing left to end, and must not fall through to the walkthrough:
    // discard clears practiceSessionId, which makes hasAlreadyEnded() false, so a late ended$
    // (a drop while voiceCall.stop() was still resolving) would otherwise reach the no-session
    // branch of endPracticeAndAnalyze and open "Preparing your feedback" — auto-advancing to a
    // report for an attempt the learner threw away.
    if (this.isEnding || this.hasAlreadyEnded() || this.isDiscarded) { return; }
    this.isEnding = true;
    this.stopCallTicker();
    this.detachBotAudio();   // let the sink go before stop() closes the track under it
    if (!this.voiceCall.isActive()) {
      // No live call (walkthrough, or already torn down) — just run the end/analysis path.
      this.endPracticeAndAnalyze(undefined, undefined, endedReason);
      return;
    }
    const result = await this.voiceCall.stop(endedReason);
    this.transcript = result?.transcript ?? this.transcript;
    this.endPracticeAndAnalyze(
      result?.micAudio, result?.mixedAudio, result?.endedReason ?? endedReason, result?.micAudioSeconds
    );
  }

  private showError(message: string) {
    this.errorMessage = message;
    this.toasterService.error(message);
    this.scrollMainToBottom();
  }

  /** True once this session's audio has been uploaded and /end posted — it must not happen twice. */
  private hasAlreadyEnded(): boolean {
    return !!this.practiceSessionId && this.endedSessionIds.has(this.practiceSessionId);
  }

  /** 403 / 404 / 429 / 503 all mean something specific to the learner. */
  /**
   * The settings paths — defaults, refine, save, reset — fail for their own reasons. Describing them
   * with describeCreateError reported a session-start failure that never happened, and turned a 404
   * on a refine into "We could not find this action."
   */
  private describeSettingsError(error: any): string {
    switch (error?.status) {
      case 403: return this.i18n.t('This action is not available for voice practice.');
      case 429: return this.i18n.t('Too many changes at once. Please try again shortly.');
      default: return this.i18n.t('We could not update your practice scenario. Please try again.');
    }
  }

  /**
   * The wait is reported two different ways: a `Retry-After` header, or plain prose in the body
   * ("Rate limit exceeded. Try again in 670 seconds."). Stage sends only the second, so reading the
   * header alone dropped the learner to "try again later" with no idea how long that meant.
   */
  private retrySecondsFrom(error: any): number {
    const header = Number(error?.headers?.get?.('Retry-After'));
    if (header > 0) { return header; }
    const detail = error?.error?.detail ?? error?.error?.message ?? '';
    const seconds = Number(/(\d+)\s*second/i.exec(String(detail))?.[1]);
    return seconds > 0 ? seconds : 0;
  }

  private describeRateLimit(error: any): string {
    const seconds = this.retrySecondsFrom(error);
    if (!seconds) {
      return this.i18n.t('You have reached the practice limit. Please try again later.');
    }
    // Over a couple of minutes, "670 seconds" is arithmetic homework. Rounded UP, so acting on the
    // number can never send the learner back before the window has actually cleared.
    if (seconds >= 120) {
      return this.i18n.t('You have reached the practice limit. Try again in %s minutes.')
        .replace('%s', String(Math.ceil(seconds / 60)));
    }
    // The number is spliced into the translated sentence, so the phrase stays one lookup.
    return this.i18n.t('You have reached the practice limit. Try again in %s seconds.')
      .replace('%s', String(seconds));
  }

  private describeCreateError(error: any): string {
    switch (error?.status) {
      case 403: return this.i18n.t('This action is not available for voice practice.');
      case 404: return this.i18n.t('We could not find this action.');
      case 429: return this.describeRateLimit(error);
      case 503: return this.i18n.t('Voice practice is temporarily unavailable. Please try again shortly.');
      default: return this.i18n.t('We could not start voice practice. Please check your connection and try again.');
    }
  }

  /**
   * Step 3 + 4 — upload the recordings, close the session, then poll for the report.
   * `audio` is the learner-mic recording (drives analysis); `playbackAudio` is the mixed clip.
   */
  endPracticeAndAnalyze(
    audio?: Blob,
    playbackAudio?: Blob,
    endedReason: PracticeEndedReason = 'user_ended',
    audioDurationSeconds?: number
  ) {
    if (this.hasAlreadyEnded()) { return; }
    if (!this.practiceSessionId) {
      // No live session (walkthrough) — keep the timed feedback screen.
      this.showScreen('feedback');
      return;
    }
    this.endedSessionIds.add(this.practiceSessionId);

    // Analysis runs off the learner's recording. Aborting before speaking leaves nothing to
    // upload, and the session then sits in `ended_awaiting_audio` for good — so there is no
    // report to wait for, and no reason to open the waiting screen or start polling.
    const hasAnalysisAudio = !!audio && audio.size > 0;

    this.isAnalyzing = hasAnalysisAudio;
    this.errorMessage = '';
    if (hasAnalysisAudio) {
      this.showScreen('feedback');
    }

    const sessionId = this.practiceSessionId;
    mixpanel.track('practice session ended', {
      practiceSessionId: sessionId,
      endedReason,
      durationSeconds: Math.round(this.callElapsedMs / 1000),
      audioDurationSeconds: audioDurationSeconds ?? null,
      turnCount: this.transcript?.length ?? 0,
      hasAnalysisAudio,
    });

    if (playbackAudio) {
      // Replay-only: fire-and-forget, it must never block or fail the report.
      this.subscriptions.add(
        this.practiceBotService.uploadPlaybackAudio(sessionId, playbackAudio).subscribe(
          () => {},
          (error) => console.log('Practice playback audio upload failed', error)
        )
      );
    }

    // ONE chain, not parallel fire-and-forget: analysis starts when the second of the two lands, so a
    // failed upload must stop here rather than ending a session that can never be analysed.
    const uploadAnalysisAudio$ = hasAnalysisAudio
      ? this.practiceBotService.uploadLearnerAudio(sessionId, audio)
      : of(null);

    this.subscriptions.add(
      uploadAnalysisAudio$
        .pipe(
          switchMap(() =>
            // The recording's own length, which the server cannot work out for itself — its
            // fallback is the wall clock, minutes longer than the track on a session that sat
            // idle between turns.
            this.practiceBotService.endPracticeSession(
              sessionId, this.transcript, endedReason, audioDurationSeconds
            )
          )
        )
        .subscribe(
          (response) => {
            this.setPracticeStatus(response);
            if (!hasAnalysisAudio) {
              // Closed the session so it is not left hanging, but polling would only repeat
              // `ended_awaiting_audio` until the timeout. The no-data screen says why.
              this.isAnalyzing = false;
              this.isEnding = false;
              this.showScreen('feebacknodata');
              this.cdRef.detectChanges();
              return;
            }
            this.pollForAnalysis();
          },
          (error) => {
            console.log('Practice end session failed', error);
            this.isAnalyzing = false;
            this.isEnding = false;
            // Allow a retry: the id is added up front to block a concurrent second end, but this
            // session was never actually closed. Leaving it marked would make hasAlreadyEnded()
            // true forever, so every later attempt returns at the guard and the learner is stuck
            // with a session they cannot finish.
            this.endedSessionIds.delete(sessionId);
            this.showError(this.i18n.t('We could not finish this practice session.'));
            mixpanel.track('practice end session failed', { practiceSessionId: sessionId, status: error?.status });
            // "Preparing your feedback" was opened before the upload started, and nothing else takes
            // the learner off it. Without this a dropped connection leaves them watching a spinner
            // for a report that is never coming — with the error message hidden behind it.
            this.showScreen('main');
            this.cdRef.detectChanges();
          }
        )
    );
  }

  /** Step 4 — poll until the analysis is complete (or failed), then show the report. */
  private pollForAnalysis() {
    // Captured before subscribing: polling runs for up to 120s and every callback below used to
    // re-read the field. A new session created in that window — or endAndDiscard() clearing it to
    // '' — would attribute the report and the analytics to the wrong id, and
    // recordCompletedAttempt('') would drop the attempt without a word.
    const sessionId = this.practiceSessionId;
    this.subscriptions.add(
      this.practiceBotService.pollPracticeAnalysis(sessionId).subscribe(
        (result) => {
          this.isAnalyzing = false;
          this.isEnding = false;
          if (result?.status === 'complete') {
            this.showAnalysis(result, sessionId);
            // A session the History list behind has never seen.
            this.noteHistoryChanged();
            // Adds this attempt to the main screen's system-message feed.
            this.recordCompletedAttempt(
              sessionId,
              new Date().toISOString(),
              result?.performance_score ?? null,
              {
                // The call that just ended, wall-clock — the same measure duration_seconds carries.
                // Without it the row reads 0:00 until the history is next re-read.
                durationSeconds: Math.round(this.callElapsedMs / 1000),
                // This attempt is one the loaded history never saw, so the count it belongs to has
                // to grow with it. Without a number the row would be labelled from its position
                // among the COMPLETED ones, which undercounts every abandoned attempt before it.
                attemptNumber: ++this.totalAttempts,
              },
            );
            // Step 5 — the captured id, not the field: see the comment at the top of this method.
            this.awardPracticePoints(sessionId);
            mixpanel.track('practice analysis complete', {
              practiceSessionId: sessionId,
              performanceScore: result?.performance_score ?? null,
            });
            this.showScreen('final');
          } else {
            this.setPracticeStatus(result);
            // A failed analysis produces no report, so the waiting screen has nothing to wait
            // for. Record the attempt as cancelled and go back to the preview, where the feed
            // says as much — leaving the learner on the spinner is a dead end.
            const failed: any = result;
            // Still a session run against this action: its totals have moved even though there is
            // no report to show for it.
            this.noteHistoryChanged();
            this.recordCancelledAttempt(
              sessionId,
              failed?.ended_at || failed?.created_at || new Date().toISOString()
            );
            if (this.endedTooEarly(result)) {
              // Nothing was said, so there is no report to write — the screen explains that,
              // which reads better than a toast over an empty report.
              this.showScreen('feebacknodata');
            } else {
              // A failure for some other reason: the copy on that screen would be wrong.
              this.showError(this.describeAnalysisFailure(result));
              this.showScreen('main');
            }
            mixpanel.track('practice analysis failed', {
              practiceSessionId: sessionId,
              status: result?.status,
              degradations: result?.degradations ?? [],
            });
          }
          this.cdRef.detectChanges();
        },
        (error) => {
          console.log('Practice analysis polling failed', error);
          this.isAnalyzing = false;
          this.isEnding = false;
          this.showError(this.i18n.t('Your feedback is taking longer than expected. Please try again later.'));
          mixpanel.track('practice analysis timed out', { practiceSessionId: sessionId });
          // Same as the failed-status branch above: the waiting screen has nothing left to wait for.
          // The session may still finish server-side, so it is NOT recorded as cancelled — it will
          // appear in the history the next time this screen loads.
          this.showScreen('main');
          this.cdRef.detectChanges();
        }
      )
    );
  }

  /** Give Foozi time to settle the session row before giving up on a `pending_session`. */
  private readonly AWARD_RETRY_DELAYS_MS = [2000, 4000, 8000];

  /**
   * Credits the action points for this attempt (APP-3275). Nothing else in the app credits them.
   *
   * Fire-and-forget on purpose: the report is already on screen and must never be blocked or
   * failed by a points call.
   *
   * NOT added to `this.subscriptions`. That bag is unsubscribed in ngOnDestroy, and unsubscribing
   * an HttpClient observable CANCELS the request in flight — so a learner who closes the modal
   * before it returns silently loses the credit until their next session. This is a one-shot HTTP
   * call that completes on its own; there is nothing to leak.
   *
   * `pending_session: true` means Foozi has not written the row as `complete` yet. Retrying is
   * what keeps the points on THIS attempt instead of letting them land on a later one. The retry
   * uses setTimeout rather than an RxJS operator so it stays outside the subscription bag too.
   */
  private awardPracticePoints(sessionId: string, attempt = 0) {
    if (!sessionId) { return; }
    this.practiceBotService
      .awardPracticeActionPoints({
        practiceSessionId: sessionId,
        screenContentId: this.screenContentId,
      })
      .subscribe(
        (response) => {
          const awarded = response?.data?.points_awarded ?? 0;
          if (awarded > 0) {
            const earned = response?.data?.earned_action_points;
            if (earned != null) {
              // The server's new total, so the badge updates now rather than after the refetch below.
              this.appState.set('currentActionPoints', earned);
              this.walletService.update(earned);
            }
            this.toasterService.success(
              this.i18n.t('+%{points} action points earned!', { points: awarded })
            );
            // Tells the shell its cached points total is stale; home-sheet-modal's ngDoCheck
            // consumes the flag and re-reads the badge from the leaderboard stats.
            this.appState.set('updateActionPoints', true);
          }
          if (response?.data?.pending_session && attempt < this.AWARD_RETRY_DELAYS_MS.length) {
            // The attempt has not settled server-side yet. Back off and ask again rather than
            // leaving the points to be bundled into some later call.
            setTimeout(
              () => this.awardPracticePoints(sessionId, attempt + 1),
              this.AWARD_RETRY_DELAYS_MS[attempt]
            );
            return;
          }
          mixpanel.track('practice points awarded', {
            practiceSessionId: sessionId,
            pointsAwarded: awarded,
            attempts: attempt + 1,
            // True here means we ran out of retries — worth watching in the dashboard.
            stillPending: !!response?.data?.pending_session,
            earnedActionPoints: response?.data?.earned_action_points ?? null,
          });
        },
        (error) => {
          // Swallowed deliberately — see the comment above. The next completed session's call
          // reconciles whatever this one missed.
          console.log('Practice points award failed', error);
          mixpanel.track('practice points award failed', {
            practiceSessionId: sessionId,
            attempts: attempt + 1,
            status: error?.status,
          });
        }
      );
  }

  /**
   * Mirrors the `status` of any practice response onto `practiceStatus`. Responses that carry no
   * status leave the last known one alone, so it always reflects the most recent one the API gave.
   */
  private setPracticeStatus(response: any): void {
    const status = response?.status;
    if (typeof status === 'string' && status) {
      this.practiceStatus = status as PracticeSessionStatus;
    }
  }

  /** Headline score for the report screen (step 6). */
  get performanceScore(): number | null {
    return this.analysis?.performance_score ?? null;
  }

  // ── The feedback screen, rendered from the analysis ────────────────────────────

  /** How full the meter is. Clamped, because a score outside 0–100 would break the bar. */
  get scoreMeterPercent(): number {
    return Math.max(0, Math.min(100, this.performanceScore ?? 0));
  }

  /** Left-hand meter legend: where this score sits against the target. */
  get scoreStandingLabel(): string {
    const score = this.performanceScore;
    if (score === null) { return ''; }
    return score >= PRACTICE_SCORE_TARGET ? this.i18n.t('On target') : this.i18n.t('Needs work');
  }

  /**
   * Everything the report loops over, derived once per analysis rather than per
   * change-detection pass. `*ngFor` compares by reference, so getters that rebuilt these arrays
   * on every call would tear down and re-render those rows on every cycle — and while the
   * recording plays, `timeupdate` fires several times a second.
   *
   * It also reconciles the two payload shapes: the live one (`metrics` + `feedback`, with SBI
   * points) and the one the API doc describes (`delivery` + `summary` + `content`).
   */
  private analysisView: {
    source: PracticeAnalysis;
    chips: PracticeDeliveryChip[];
    strengths: PracticeFeedbackPoint[];
    improvements: PracticeFeedbackPoint[];
    deliveryLines: string[];
    chart: PracticeTrendChart;
  };

  private get view() {
    if (!this.analysisView || this.analysisView.source !== this.analysis) {
      this.analysisView = this.buildAnalysisView();
    }
    return this.analysisView;
  }

  private buildAnalysisView() {
    const analysis: any = this.analysis;
    const metrics = analysis?.metrics;              // live shape
    const delivery = analysis?.delivery;            // documented shape
    const feedback = analysis?.feedback;
    const summary = analysis?.summary;

    const chips: PracticeDeliveryChip[] = [];
    // Only the metrics this session reported: no hedges should mean no chip, not "0 hedges".
    const wpm = metrics?.speaking_rate?.wpm ?? delivery?.pace_wpm;
    const pauses = metrics?.pauses?.count ?? delivery?.pauses?.count;
    const hedges = metrics?.linguistic?.hedges?.count ?? delivery?.hedges?.count;
    const fillers = metrics?.linguistic?.fillers?.count ?? delivery?.fillers?.count;
    if (wpm) {
      chips.push({ text: `${Math.round(wpm)} ${this.i18n.t('wpm')}`, tone: 'green', icon: 'pace' });
    }
    if (pauses) {
      chips.push({ text: this.countLabel(pauses, 'long pause', 'long pauses'), tone: 'pink', icon: 'pause' });
    }
    if (hedges) {
      chips.push({ text: this.countLabel(hedges, 'hedge', 'hedges'), tone: 'pink', icon: 'speech' });
    }
    if (fillers) {
      chips.push({ text: this.countLabel(fillers, 'filler', 'fillers'), tone: 'pink', icon: 'speech' });
    }

    const content: any[] = analysis?.content ?? [];
    const rawStrengths = feedback?.strengths
      ?? summary?.strengths
      ?? content.filter((criterion) => criterion?.verdict === 'met');
    const rawImprovements = feedback?.content_improvements
      ?? summary?.improvements
      ?? content.filter((criterion) => criterion?.verdict && criterion.verdict !== 'met');

    // Chart geometry as percentages of the plot box: x across, y up from the baseline. The
    // template positions the dots and their value labels with these directly, so they line up
    // with the polyline without either having to know the panel's pixel width.
    const trend = analysis?.trend ?? [];
    const points = trend.map((entry, index) => {
      const score = Math.max(0, Math.min(100, entry?.performance_score ?? 0));
      return {
        // A lone attempt sits in the middle rather than pinned to the left edge.
        x: trend.length === 1 ? 50 : (index / (trend.length - 1)) * 100,
        y: score,
        score: entry?.performance_score ?? 0,
        label: `S${index + 1}`,
      };
    });

    return {
      source: this.analysis,
      chips,
      strengths: (rawStrengths ?? []).map((point) => this.toFeedbackPoint(point)),
      improvements: (rawImprovements ?? []).map((point) => this.toFeedbackPoint(point)),
      deliveryLines: feedback?.delivery_feedback ?? delivery?.observations ?? [],
      chart: {
        // SVG y runs downwards, so the polyline flips what the CSS percentages express.
        line: points.map((point) => `${point.x.toFixed(1)},${(100 - point.y).toFixed(1)}`).join(' '),
        points,
        ticks: TREND_TICKS.map((value) => ({ value, y: value })),
      },
    };
  }

  /**
   * One point of feedback, however the payload expressed it: an SBI object, a bare sentence, or
   * a rubric criterion. The template renders whichever parts came back.
   */
  private toFeedbackPoint(raw: any): PracticeFeedbackPoint {
    if (typeof raw === 'string') {
      return { situation: '', behavior: '', impact: raw, suggestion: '', label: '' };
    }
    return {
      situation: raw?.situation ?? '',
      behavior: raw?.behavior ?? raw?.evidence?.[0] ?? '',
      impact: raw?.impact ?? '',
      suggestion: raw?.suggestion ?? '',
      label: raw?.label ?? '',
    };
  }

  private countLabel(count: number, singular: string, plural: string): string {
    return `${count} ${this.i18n.t(count === 1 ? singular : plural)}`;
  }

  /** Delivery metrics as chips under the score. */
  get deliveryChips(): PracticeDeliveryChip[] {
    return this.view.chips;
  }

  /** What went well — the headline cards and the detailed SBI blocks read from this. */
  get strengths(): PracticeFeedbackPoint[] {
    return this.view.strengths;
  }

  get improvements(): PracticeFeedbackPoint[] {
    return this.view.improvements;
  }

  /** Sentences about how it was said, not what was said. */
  get deliveryLines(): string[] {
    return this.view.deliveryLines;
  }

  get nextFocus(): string {
    return (this.analysis as any)?.feedback?.next_focus ?? this.analysis?.summary?.next_focus ?? '';
  }

  /** How this attempt compares with the last — the session summary line. */
  get trendComment(): string {
    return (this.analysis as any)?.feedback?.trend_comment ?? '';
  }

  get trend(): Array<{ practice_session_id: string; performance_score: number }> {
    return this.analysis?.trend ?? [];
  }

  get trendChart(): PracticeTrendChart {
    return this.view.chart;
  }

  /** Non-empty when a component of the analysis fell back — worth a soft note. */
  get degradations(): string[] {
    return this.analysis?.degradations ?? [];
  }

  /** Stable identity for *ngFor so existing rows are not re-rendered. */
  trackFeedbackPoint(index: number) {
    return index;
  }

  /**
   * Shows one attempt's analysis. The recording belongs to that attempt, so any player state
   * from a previously viewed one is dropped rather than replayed under the new score.
   */
  private showAnalysis(result: PracticeAnalysis, sessionId: string): void {
    this.setPracticeStatus(result);
    this.analysis = result;
    this.analysisSessionId = sessionId || '';
    this.stopPlayback();
    this.playbackUrl = '';
    // The server says whether there is anything to play. Absent — an older deploy — it is assumed
    // there is, which is what this always did; loadPlaybackUrl still hides the player if the
    // recording turns out not to be there.
    this.hasPlaybackAudio = result?.audio_available !== false;
    this.playbackSeconds = 0;
    // The track's own length where the server has it, and the attempt's duration otherwise, so the
    // player shows a time before the learner presses play — the media probe cannot run until the
    // file has loaded. onPlaybackLoaded replaces this with the real length once it can.
    // A POSITIVE length only. Zero is what an attempt recorded before any of this reports, and
    // taking it as authoritative would leave the scrubber measuring against nothing — a bar that
    // never moves and a label stuck at 00:00, with the media never asked.
    const reported = result?.audio_duration_seconds;
    this.playbackDurationFromServer = typeof reported === 'number' && reported > 0;
    this.playbackDurationSeconds = this.playbackDurationFromServer
      ? reported
      : (this.completedAttempts.find((attempt) => attempt.id === sessionId)?.durationSeconds || 0);
    // Already authoritative when the server gave one — no need to correct it off the media.
    this.playbackDurationProbed = this.playbackDurationFromServer;
    // Back to the first three: the list is ordered around the attempt on screen, and that has just
    // changed. If the newly opened one sits further down, See more reveals it — outlined, so the
    // learner can see which row they are looking at.
    this.recordingsShown = this.RECORDINGS_STEP;
  }

  // ── Other recordings, listed under the player ─────────────────────────────────

  /**
   * Every other completed attempt for this action, newest first.
   *
   * The attempt being shown is left out on purpose: its own recording is already playing in the
   * middle of this report, so listing it again would offer the learner a link back to where they
   * are. completedAttempts is oldest-first for the message feed, so this reverses it — looking back,
   * the most recent attempt is the one worth reaching first.
   */
  /**
   * Every completed attempt for this action, newest first — the one being shown included, so the
   * learner can see it in context. completedAttempts is oldest-first for the message feed, so this
   * reverses it: looking back, the most recent is the one worth reaching first.
   */
  get otherRecordings(): PracticeCompletedAttempt[] {
    return this.completedAttempts.slice().reverse();
  }

  /** Three at a time, until See more asks for the rest. */
  get visibleRecordings(): PracticeCompletedAttempt[] {
    return this.otherRecordings.slice(0, this.recordingsShown);
  }

  // ── Playing one row's recording ───────────────────────────────────────────────
  // One <audio> element serves whichever row is playing rather than N of them: only one can play at
  // a time, and a media element per row is a lot of them on a long history.

  /** The row currently loaded into the player, playing or paused. */
  rowPlayingId = '';
  rowPlaybackSeconds = 0;
  rowPlaybackDuration = 0;
  isRowPlaying = false;
  /**
   * Whether anything in here changed what a History row would show — a session practised, or a
   * recording deleted.
   *
   * Reported on dismiss, so the list behind re-reads only when there is something to re-read.
   * Opening a report to read it and closing again is the common case and should cost nothing.
   */
  private historyChanged = false;

  /** The row whose URL is being fetched, so its control can say so. */
  loadingRowId = '';
  /**
   * The row asked to play but not yet audible.
   *
   * Two waits sit between the tap and the first sound, and neither is visible on its own: the
   * presigned URL is fetched per play, and the browser then has to load enough of the file to start.
   * `playing` — which is what flips the icon to pause — fires at the end of both. Left unmarked, the
   * control sat there looking untapped for seconds, and the learner tapped it again.
   */
  startingRowId = '';
  /**
   * Presigned playback URLs, by attempt, while they are still valid.
   *
   * Signing per play is not just the round trip. Each signature is a different URL, so the browser
   * files the media under a new key and downloads the whole recording again — every replay of a clip
   * just heard pays for it twice. Reusing the URL while it holds lets the second play come from the
   * HTTP cache, which is where the seconds actually go.
   */
  private playbackUrls = new Map<string, { url: string; expiresAt: number }>();
  /**
   * The least life a stored signature must have left to be worth reusing.
   *
   * A floor, not the whole rule: what a signature really has to outlive is the play itself, since
   * the browser fetches a long recording in pieces as it goes and a signature that dies partway
   * takes the rest of the audio with it. So the requirement is the recording's own length, and this
   * is the minimum underneath it — slack for the gap between deciding and fetching, and for a device
   * clock that does not agree with the signing server's.
   */
  private readonly URL_MIN_REMAINING_MS = 60000;

  /** Label for a row: "Session 3 · 64", numbered from the server rather than the array index. */
  recordingLabel(attempt: PracticeCompletedAttempt, index: number): { label: string; score: number | null } {
  // Rows run newest first, so without attempt_number the fallback counts back from the total.
  const number = attempt?.attemptNumber ?? (this.otherRecordings.length - index);
  const label = `${this.i18n.t('Session')} ${number}`;
  const score = attempt?.score ?? null;
  return { label, score };
}

  /**
   * How long this attempt ran. Fixed — the same figure playing or not.
   *
   * It is an attribute of the row, like the session number and the score, and the column is read
   * down: 5:48 / 6:40 / 7:05 is how the learner picks which attempt to replay. A row that starts
   * counting while the others stand still takes its own column away and unsettles the list; where
   * the playhead is is the bar's job, right beside it.
   *
   * Deliberately duration_seconds rather than the track's own length, which the bar uses. That is
   * wall-clock (created → ended) and over-reads a session the learner paused, but it is the figure
   * the row showed before playing — swapping it for a truer one the moment they press play is the
   * same instability by another route.
   */
  recordingTime(attempt: PracticeCompletedAttempt): string {
    return this.formatDuration(attempt?.durationSeconds || 0, { padMinutes: false });
  }

  /**
   * The length to measure the bar and a scrub against: the track's own where the browser knows it.
   *
   * Not duration_seconds, which over-reads a paused session — the fill would stop short of the end.
   */
  private recordingTotalSeconds(attempt: PracticeCompletedAttempt): number {
    if (this.rowPlayingId === attempt?.id && this.rowPlaybackDuration) {
      return this.rowPlaybackDuration;
    }
    return attempt?.durationSeconds || 0;
  }

  /** How far through, as a percentage, for the row's bar. */
  recordingProgress(attempt: PracticeCompletedAttempt): number {
    if (this.rowPlayingId !== attempt?.id) { return 0; }
    const total = this.recordingTotalSeconds(attempt);
    if (!total) { return 0; }
    return Math.min(100, (this.rowPlaybackSeconds / total) * 100);
  }

  /**
   * Moves the playhead to the point tapped on a row's bar.
   *
   * The bar sat inside the button that reopens the report, so a tap on it opened the summary instead
   * of scrubbing — and a button inside a button is not something the browser honours anyway. It is
   * its own control now; a row that is not the one loaded keeps the old behaviour, since there is no
   * playhead there to move.
   */
  seekRecording(attempt: PracticeCompletedAttempt, event: MouseEvent): void {
    event.stopPropagation();
    const id = attempt?.id;
    if (!id) { return; }
    if (this.rowPlayingId !== id) {
      this.openPerformanceSummary(id);
      return;
    }
    const element = this.rowAudio?.nativeElement;
    const bar = event.currentTarget as HTMLElement;
    const width = bar?.getBoundingClientRect?.().width || 0;
    const total = this.recordingTotalSeconds(attempt);
    if (!element || !width || !total) { return; }

    const offset = event.clientX - bar.getBoundingClientRect().left;
    const ratio = Math.min(1, Math.max(0, offset / width));
    element.currentTime = ratio * total;
    // Straight away rather than waiting for the next timeupdate, which is throttled to ~4/second and
    // never fires at all while the recording is paused.
    this.rowPlaybackSeconds = element.currentTime;
    this.cdRef.detectChanges();
  }

  isRowPlayingNow(attempt: PracticeCompletedAttempt): boolean {
    return this.isRowPlaying && this.rowPlayingId === attempt?.id;
  }

  /** Tapped, and working on it: the URL round trip, then the media loading. */
  isRowStarting(attempt: PracticeCompletedAttempt): boolean {
    const id = attempt?.id;
    if (!id) { return false; }
    return this.loadingRowId === id || (this.startingRowId === id && !this.isRowPlaying);
  }

  onRowPlaying(): void {
    this.isRowPlaying = true;
    this.startingRowId = '';
    // One recording at a time. The report's own playback and a row's are separate players with
    // separate controls, and nothing stopped them talking over each other.
    this.pauseMainPlayback();
  }

  onRowPaused(): void {
    this.isRowPlaying = false;
    this.startingRowId = '';
  }

  /**
   * Pauses a player without unloading it, so it keeps its place for when the learner comes back.
   *
   * Enforced where sound actually starts rather than where play is asked for: whichever player
   * begins, the other stops, however it was started.
   */
  private pauseRowPlayback(): void {
    const element = this.rowAudio?.nativeElement;
    if (element && !element.paused) { element.pause(); }
    this.isRowPlaying = false;
    this.startingRowId = '';
  }

  private pauseMainPlayback(): void {
    const element = this.playbackAudio?.nativeElement;
    if (element && !element.paused) { element.pause(); }
    this.isPlaybackPlaying = false;
    this.playbackStarting = false;
  }

  /**
   * Plays one row's recording, or pauses it if it is the one already playing.
   *
   * The URL is presigned and short-lived, so it is fetched per play rather than held — including on
   * a second play of the same row, whose earlier URL may have expired by then.
   */
  toggleRowPlayback(attempt: PracticeCompletedAttempt, event: Event): void {
    event.stopPropagation();          // the row opens the report; the play button must not
    const id = attempt?.id;
    if (!id || this.loadingRowId) { return; }
    // The server said there is no recording for this attempt. Without this the tap costs a round
    // trip to be told the same thing, and the learner gets a spinner that ends in nothing.
    if (attempt?.audioAvailable === false) {
      this.toasterService.warning(this.i18n.t('There is no recording for this attempt.'));
      return;
    }

    const element = this.rowAudio?.nativeElement;
    if (this.rowPlayingId === id && element) {
      if (element.paused) {
        // Loaded already, but resuming still waits on the browser: mark it until `playing` lands.
        this.startingRowId = id;
        element.play().catch(() => this.onRowPlaybackError());
      } else {
        element.pause();
      }
      return;
    }

    this.startingRowId = id;

    // A signature from an earlier play that will outlast this one: the same URL, so the media comes
    // from the browser cache rather than down the wire again.
    const cached = this.playbackUrls.get(id);
    if (cached && this.signatureOutlastsPlay(cached.expiresAt, attempt)) {
      this.playRowFrom(id, cached.url, attempt);
      return;
    }

    this.loadingRowId = id;
    this.subscriptions.add(
      this.practiceBotService.getPlaybackAudioUrl(id).subscribe(
        (response: any) => {
          this.loadingRowId = '';
          const url = response?.url || '';
          if (!url) {
            // 404 / nothing stored — say so rather than leaving a button that does nothing.
            this.startingRowId = '';
            this.showError(this.i18n.t('That attempt has no recording.'));
            this.cdRef.detectChanges();
            return;
          }
          // Only on the server's word. A signature with no stated life is one we do not know the
          // life of, and inventing a figure risks handing back a URL that has already lapsed — the
          // player then fails in front of the learner, where re-signing costs one short request.
          const ttlSeconds = Number(response?.expires_in);
          if (ttlSeconds > 0) {
            // Stored as the server stated it. Whether that is enough is a question about the play
            // being started, so it is asked at the point of reuse rather than baked in here.
            this.playbackUrls.set(id, { url, expiresAt: Date.now() + ttlSeconds * 1000 });
          }
          this.playRowFrom(id, url, attempt);
        },
        (error) => {
          console.log('Row playback unavailable', error);
          this.loadingRowId = '';
          this.startingRowId = '';
          this.showError(this.i18n.t('We could not play that recording.'));
          this.cdRef.detectChanges();
        },
      ),
    );
  }

  /**
   * Whether a stored signature will still be valid at the END of this recording, not just at its
   * start — the point being that the browser goes on fetching a long file while it plays.
   *
   * duration_seconds over-reads a session the learner paused, which is the safe direction: it asks
   * for more life than the audio strictly needs.
   */
  private signatureOutlastsPlay(expiresAt: number, attempt: PracticeCompletedAttempt): boolean {
    const playMs = (attempt?.durationSeconds || 0) * 1000;
    return expiresAt - Math.max(this.URL_MIN_REMAINING_MS, playMs) > Date.now();
  }

  /** Points the player at a URL and starts it. Shared by the signed and the cached paths. */
  private playRowFrom(id: string, url: string, attempt: PracticeCompletedAttempt): void {
    this.rowPlayingId = id;
    this.rowPlaybackSeconds = 0;
    this.rowPlaybackDuration = attempt?.durationSeconds || 0;
    this.rowPlaybackUrl = url;
    this.cdRef.detectChanges();
    // The element is created by the URL binding above, so play on the next tick.
    setTimeout(() => this.rowAudio?.nativeElement?.play().catch(() => this.onRowPlaybackError()), 0);
  }

  onRowPlaybackTime(): void {
    const element = this.rowAudio?.nativeElement;
    if (!element) { return; }
    this.rowPlaybackSeconds = element.currentTime || 0;
    if (isFinite(element.duration) && element.duration > 0) {
      // The real track length, once the browser knows it — duration_seconds is session wall-clock
      // and over-reads whenever the learner paused.
      this.rowPlaybackDuration = element.duration;
    }
  }

  onRowPlaybackEnded(): void {
    this.isRowPlaying = false;
    this.startingRowId = '';
    this.rowPlaybackSeconds = 0;
  }

  onRowPlaybackError(): void {
    // A signature can lapse early, or be rejected outright. Drop it so the next tap signs a new one
    // rather than replaying the same failure.
    if (this.rowPlayingId) { this.playbackUrls.delete(this.rowPlayingId); }
    this.isRowPlaying = false;
    this.startingRowId = '';
    this.rowPlayingId = '';
    this.rowPlaybackUrl = '';
    this.showError(this.i18n.t('We could not play that recording.'));
    this.cdRef.detectChanges();
  }

  /** Stops whatever a row was playing — on leaving the screen, or when the row is deleted. */
  private stopRowPlayback(): void {
    const element = this.rowAudio?.nativeElement;
    if (element && !element.paused) { element.pause(); }
    this.isRowPlaying = false;
    this.startingRowId = '';
    this.rowPlayingId = '';
    this.rowPlaybackUrl = '';
    this.rowPlaybackSeconds = 0;
  }

  /** True for the attempt whose feedback is on screen — the row is outlined to say so. */
  isActiveRecording(attempt: PracticeCompletedAttempt): boolean {
    return !!attempt?.id && attempt.id === this.analysisSessionId;
  }

  /** More rows held than shown, or more still on the server. */
  get canShowMoreRecordings(): boolean {
    return this.otherRecordings.length > this.recordingsShown || this.hasMoreAttempts;
  }

  /** Only worth offering once more than the first three are open. */
  get canHideRecordings(): boolean {
    return this.recordingsShown > this.RECORDINGS_STEP;
  }

  /**
   * Reveals the next three. Fetches another page first when the rows in hand have run out, so
   * See more keeps working past the end of what the report's own load brought.
   */
  showMoreRecordings(): void {
    this.recordingsShown += this.RECORDINGS_STEP;
    if (this.recordingsShown > this.otherRecordings.length && this.hasMoreAttempts) {
      this.loadMoreRecordings();
    }
  }

  hideExtraRecordings(): void {
    this.recordingsShown = this.RECORDINGS_STEP;
  }

  /**
   * Deletes one recording, with its report.
   *
   * Deleting the attempt currently on screen leaves nothing to show, so the modal goes back where
   * the report was opened from — the practice bot's main screen, or out of the modal entirely when
   * the report was opened straight onto (from History or the action feed).
   */
  deleteRecording(attempt: PracticeCompletedAttempt, event: Event): void {
    event.stopPropagation();          // the row opens the report; the bin must not
    const id = attempt?.id;
    if (!id || this.deletingRecordingId) { return; }
    const wasActive = this.isActiveRecording(attempt);

    this.toasterService.showConfirm(
      this.i18n.t('Delete this recording and its feedback? This cannot be undone.'),
      () => {
        this.deletingRecordingId = id;
        this.subscriptions.add(
          this.practiceBotService.deletePracticeSession(id).subscribe(
            () => {
              this.deletingRecordingId = '';
              // New arrays, not splices: the timeline caches these by reference.
              this.completedAttempts = this.completedAttempts.filter((a) => a.id !== id);
              this.cancelledAttempts = this.cancelledAttempts.filter((a) => a.id !== id);
              this.playbackUrls.delete(id);   // its audio is gone; the signature cannot outlive it
              this.dropFromTrend(id);         // and its score comes off the graph above the list
              this.noteHistoryChanged();      // one fewer attempt than the list behind is showing
              // The server's list is one shorter now, so the paging state has to follow it down.
              // Left alone, the next page is asked for at an offset that no longer points where it
              // did — the window has shifted by one, so one attempt is stepped over and never
              // appears for the rest of this modal session — and the counter reads one short of its
              // own total ("4/5") with no page left to fetch that could close the gap.
              this.attemptsOffset = Math.max(0, this.attemptsOffset - 1);
              this.totalAttempts = Math.max(0, this.totalAttempts - 1);
              this.trackNewestAttempt();
              // Its audio is gone with it.
              if (this.rowPlayingId === id) { this.stopRowPlayback(); }
              this.toasterService.success(this.i18n.t('Recording deleted'));
              if (wasActive) {
                this.analysis = null;
                this.analysisSessionId = '';
                this.stopPlayback();
                this.playbackUrl = '';
                this.goBack();
              }
              this.cdRef.detectChanges();
            },
            (error) => {
              console.log('Practice recording delete failed', error);
              this.deletingRecordingId = '';
              this.showError(this.i18n.t('We could not delete that recording.'));
              this.cdRef.detectChanges();
            },
          ),
        );
      },
    );
  }

  /**
   * Takes a deleted attempt out of the trend the report on screen was drawn from.
   *
   * The graph, the session count beside its title and the geometry behind both come from the
   * analysis payload — the server's answer from before the delete. Nothing re-reads it when a row
   * is deleted underneath it, so the recording the learner just removed went on being plotted, with
   * its score, next to rows that no longer include it.
   *
   * A new object rather than a splice: the report's derived view is cached against the analysis by
   * reference, and an array edited in place would leave the old chart on screen.
   */
  private dropFromTrend(sessionId: string): void {
    const trend = this.analysis?.trend;
    if (!Array.isArray(trend)) { return; }
    const kept = trend.filter((entry: any) => entry?.practice_session_id !== sessionId);
    if (kept.length === trend.length) { return; }
    this.analysis = { ...(this.analysis as any), trend: kept };
  }

  /** The total this report is one of — every completed attempt, including the one on screen. */
  get completedSessionCount(): number {
    return this.completedAttempts.length;
  }

  /**
   * The next page of this action's attempts.
   *
   * Both screens page the SAME list into the same completedAttempts, so they share one offset and
   * one fetch. With one each, whichever screen was behind spent its next requests re-reading rows
   * the other had already loaded before it reached anything new.
   */
  loadMoreRecordings(): void {
    this.fetchAttemptPage();
  }

  trackRecording(_index: number, attempt: PracticeCompletedAttempt) {
    return attempt?.id ?? _index;
  }

  /** False when the analysis carried no detail at all — the panel says so instead of sitting empty. */
  get hasDetailedFeedback(): boolean {
    return !!(
      this.strengths.length
      || this.improvements.length
      || this.deliveryLines.length
      || this.nextFocus
    );
  }

  // ── Recording playback ────────────────────────────────────────────────────────

  /**
   * The playback URL is presigned and short-lived, so it is fetched when the report opens
   * rather than held from earlier, and re-fetched if it has expired by the time Play is hit.
   */
  private loadPlaybackUrl(): void {
    // An in-flight fetch already carries playWhenLoaded, so a tap during one is not lost.
    if (!this.analysisSessionId || this.playbackUrl || this.isLoadingPlayback) { return; }

    this.isLoadingPlayback = true;
    this.hasPlaybackAudio = true;
    this.subscriptions.add(
      this.practiceBotService.getPlaybackAudioUrl(this.analysisSessionId).subscribe(
        (response) => {
          this.isLoadingPlayback = false;
          this.playbackUrl = response?.url || '';
          // 404 / no recording stored — hide the player rather than offer a dead button.
          this.hasPlaybackAudio = !!this.playbackUrl;
          this.cdRef.detectChanges();
          if (this.playWhenLoaded && this.playbackUrl) {
            this.playWhenLoaded = false;
            // The element is created by the URL binding above, so play on the next tick. Without
            // this the tap only loaded, and the learner had to press play a second time.
            setTimeout(() => this.playbackAudio?.nativeElement?.play()
              .catch(() => this.onPlaybackStartFailed()), 0);
            return;
          }
          this.playWhenLoaded = false;
          this.playbackStarting = false;
        },
        (error) => {
          this.isLoadingPlayback = false;
          this.hasPlaybackAudio = false;
          this.playWhenLoaded = false;
          this.playbackStarting = false;
          console.log('Practice playback unavailable', error);
          this.cdRef.detectChanges();
        }
      )
    );
  }

  togglePlayback(): void {
    const element = this.playbackAudio?.nativeElement;
    if (!element) {
      // The URL has not arrived yet: fetch it and start when it does.
      this.playbackStarting = true;
      this.playWhenLoaded = true;
      this.loadPlaybackUrl();
      return;
    }
    if (element.paused) {
      // Loaded already, but the browser still has to have enough of the file to make a sound.
      this.playbackStarting = true;
      element.play().catch((error) => {
        console.log('Practice playback failed to start', error);
        this.onPlaybackStartFailed();
        this.showError(this.i18n.t('We could not play that recording.'));
      });
    } else {
      element.pause();
    }
  }

  private onPlaybackStartFailed(): void {
    this.playbackStarting = false;
    this.playWhenLoaded = false;
    this.cdRef.detectChanges();
  }

  onPlaybackPlaying(): void {
    this.isPlaybackPlaying = true;
    this.playbackStarting = false;
    // The other half of the same rule — see onRowPlaying.
    this.pauseRowPlayback();
  }

  onPlaybackPaused(): void {
    this.isPlaybackPlaying = false;
    this.playbackStarting = false;
  }

  onPlaybackTimeUpdate(): void {
    const element = this.playbackAudio?.nativeElement;
    this.playbackSeconds = element?.currentTime || 0;
  }

  onPlaybackLoaded(): void {
    // The server's length wins where there is one — the element's own is what this handler exists
    // to work around.
    if (this.playbackDurationFromServer) { return; }
    const element = this.playbackAudio?.nativeElement;
    const duration = element?.duration;
    if (Number.isFinite(duration) && duration > 0) {
      this.playbackDurationSeconds = duration;
      return;
    }
    // A stream still being sized reports Infinity/NaN — leave the label at 00:00 for now.
    this.playbackDurationSeconds = 0;

    // MediaRecorder writes a streaming container with NO duration in its header, because it cannot
    // know the length until recording stops. Chrome fills one in; Safari reports 0 and leaves the
    // scrubber dead. Seeking past the end makes the browser index the file, after which `duration`
    // is real. Once per load — the seek fires this handler again.
    if (!element || this.playbackDurationProbed) { return; }
    this.playbackDurationProbed = true;

    const onDurationKnown = () => {
      if (!Number.isFinite(element.duration)) { return; }
      element.removeEventListener('durationchange', onDurationKnown);
      this.playbackDurationSeconds = element.duration;
      try { element.currentTime = 0; } catch (e) { /* nothing to rewind */ }
      this.cdRef.detectChanges();
    };
    element.addEventListener('durationchange', onDurationKnown);
    try {
      element.currentTime = 1e101;   // far past any real end
    } catch (e) {
      element.removeEventListener('durationchange', onDurationKnown);
    }
  }

  onPlaybackEnded(): void {
    this.isPlaybackPlaying = false;
    this.playbackStarting = false;
    this.playbackSeconds = 0;
  }

  /** A presigned URL that expired mid-session: drop it so the next Play re-fetches. */
  onPlaybackError(): void {
    this.isPlaybackPlaying = false;
    this.playbackStarting = false;
    this.playWhenLoaded = false;
    this.playbackUrl = '';
    this.playbackSeconds = 0;
    this.playbackDurationProbed = false;
  }

  /** Click anywhere on the bar to seek there. */
  seekPlayback(event: MouseEvent): void {
    const element = this.playbackAudio?.nativeElement;
    if (!element || !this.playbackDurationSeconds) { return; }
    const bar = event.currentTarget as HTMLElement;
    const ratio = (event.clientX - bar.getBoundingClientRect().left) / (bar.offsetWidth || 1);
    element.currentTime = Math.max(0, Math.min(1, ratio)) * this.playbackDurationSeconds;
  }

  get playbackProgressPercent(): number {
    if (!this.playbackDurationSeconds) { return 0; }
    return Math.min(100, (this.playbackSeconds / this.playbackDurationSeconds) * 100);
  }

  /** Elapsed once playing, total duration before that — the same slot in the design. */
  get playbackTimeLabel(): string {
    return this.formatDuration(this.playbackSeconds || this.playbackDurationSeconds);
  }

  private stopPlayback(): void {
    const element = this.playbackAudio?.nativeElement;
    if (element && !element.paused) { element.pause(); }
    this.isPlaybackPlaying = false;
  }

  formatPracticedAt(at: string): string {
    if (!at) { return ''; }
    const when = new Date(at);
    if (isNaN(when.getTime())) { return ''; }
    // The learner's language, not the browser's — see I18n.locale.
    return when.toLocaleString(this.i18n.locale, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  }

  /** The newest attempt's timestamp, for the "did they ever practise" checks. */
  get practicedAtLabel(): string {
    return this.formatPracticedAt(this.lastPracticedAt);
  }

  get hasPreviousPractice(): boolean {
    return this.completedAttempts.length > 0;
  }

  trackAttempt(index: number, attempt: { id: string }) {
    return attempt?.id ?? index;
  }

  /**
   * The message belongs to the scenario it was recorded against, so it shows until a NEW scenario
   * arises: starting another practice mints a new session and hides it, and it comes back — with
   * the new date/time — once that session is saved. A discarded session never shows one, because
   * discard clears practiceSessionId without ever setting lastSessionId.
   */
  get showPracticedMessage(): boolean {
    if (!this.completedAttempts.length && !this.cancelledAttempts.length) { return false; }
    // No live session, or the live one has resolved — completed with a report, or cancelled.
    if (!this.practiceSessionId) { return true; }
    return this.lastSessionId === this.practiceSessionId
      || this.cancelledAttempts.some((attempt) => attempt.id === this.practiceSessionId);
  }

  /**
   * The conversation and the practice attempts as one chronological list, so an attempt that
   * happened mid-conversation reads where it happened rather than after everything.
   *
   * Derived once per change to its inputs: `*ngFor` compares by reference, and rebuilding this
   * on every change-detection pass would re-render every message.
   */
  get timeline(): PracticeTimelineItem[] {
    const cache = this.timelineCache;
    if (cache
      && cache.turns === this.refinementHistory
      && cache.completed === this.completedAttempts
      && cache.cancelled === this.cancelledAttempts
      && cache.showAttempts === this.showPracticedMessage) {
      return cache.items;
    }

    const items: PracticeTimelineItem[] = this.refinementHistory.map((turn) => ({
      kind: 'turn' as const,
      // Turns stored before timestamps were recorded sort to the start, keeping their own order.
      time: this.timeValue(turn.at),
      turn,
    }));
    if (this.showPracticedMessage) {
      this.attemptFeed.forEach((attempt) => items.push({
        kind: 'attempt' as const,
        time: this.timeValue(attempt.at),
        attempt,
      }));
    }
    // Array.prototype.sort is stable, so same-time items keep the order they were added in.
    items.sort((a, b) => a.time - b.time);

    this.timelineCache = {
      turns: this.refinementHistory,
      completed: this.completedAttempts,
      cancelled: this.cancelledAttempts,
      showAttempts: this.showPracticedMessage,
      items,
    };
    return items;
  }

  private timeValue(at: string): number {
    const time = new Date(at || 0).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  /** Stable identity so existing rows survive a rebuild of the list. */
  trackTimelineItem(index: number, item: PracticeTimelineItem) {
    return item.kind === 'attempt' ? `attempt-${item.attempt?.id}` : `turn-${index}`;
  }

  /**
   * The feed under the scenario: every attempt in the order it happened, completed ones carrying
   * a score and a link to their report, cancelled ones just saying when they were.
   */
  // ── The main screen's history, condensed ──────────────────────────────────────

  /**
   * The main screen opens on the last few entries rather than the whole history. A learner arriving
   * to practise wants the scenario and the button, not a scroll back through every attempt; the
   * arrow opens it out for anyone who does.
   */
  timelineExpanded = false;
  private readonly TIMELINE_CONDENSED = 3;
  /** How many attempts have been fetched, which is the offset the next page starts at. */
  private attemptsOffset = 0;
  /** Whether the server has older attempts than the ones held. */
  hasMoreAttempts = false;
  isLoadingAttempts = false;
  /** Every attempt on this action, including ones still active or analyzing. */
  totalAttempts = 0;

  /** The tail of the timeline — the newest entries, which are the ones worth landing on. */
  get visibleTimeline(): PracticeTimelineItem[] {
    const all = this.timeline;
    if (this.timelineExpanded) { return all; }
    return all.length > this.TIMELINE_CONDENSED ? all.slice(all.length - this.TIMELINE_CONDENSED) : all;
  }

  /** How many of this action's attempts have been rendered so far — for "12/30". */
  get loadedAttemptCount(): number {
    return this.completedAttempts.length + this.cancelledAttempts.length;
  }

  /** Whether anything is being held back — either not yet shown, or not yet fetched. */
  get hasEarlierTimeline(): boolean {
    return this.timeline.length > this.visibleTimeline.length || this.hasMoreAttempts;
  }

  toggleTimeline(): void {
    this.timelineExpanded = !this.timelineExpanded;
    // Expanding pushes the button below the fold; land on the newest entry either way.
    setTimeout(() => this.scrollMainToBottom(), 0);
  }

  /**
   * "Show earlier history" — the next twenty attempts from the server.
   *
   * Opening the list first shows what is already held; only once that is exhausted does this go
   * back for more, so the first tap never waits on a request it does not need.
   */
  showEarlierTimeline(): void {
    if (!this.timelineExpanded) {
      this.timelineExpanded = true;
      setTimeout(() => this.scrollMainToBottom(), 0);
      return;
    }
    if (this.hasMoreAttempts) { this.fetchAttemptPage(); }
  }

  get attemptFeed(): Array<{ id: string; at: string; score: number | null; cancelled: boolean }> {
    return [
      ...this.completedAttempts.map((attempt) => ({ ...attempt, cancelled: false })),
      ...this.cancelledAttempts.map((attempt) => ({ ...attempt, cancelled: true })),
    ].sort((a, b) => new Date(a.at || 0).getTime() - new Date(b.at || 0).getTime());
  }

  /** Performance summary link — reopens the report for one completed attempt. */
  openPerformanceSummary(sessionId?: string) {
    const targetId = sessionId || this.lastSessionId;
    if (!targetId || this.isLoadingSummary) { return; }
    // Already the analysis on hand — no need to refetch. Keyed on the attempt it belongs to,
    // so a different attempt in the feed still fetches its own.
    if (this.analysis && targetId === this.analysisSessionId) {
      this.showScreen('final');
      return;
    }
    this.isLoadingSummary = true;
    this.openingSummaryId = targetId;
    this.subscriptions.add(
      this.practiceBotService.getPracticeSession(targetId).pipe(
        // Without this, a request the server never answers leaves the link stuck on
        // "Opening…" for good: the loading flag also blocks a second attempt, so there is
        // no way back. A timeout routes it to the error handler, which clears both.
        timeout(SUMMARY_TIMEOUT_MS)
      ).subscribe(
        (result) => {
          this.isLoadingSummary = false;
          this.openingSummaryId = '';
          if (result?.status === 'complete') {
            this.showAnalysis(result, targetId);
            mixpanel.track('practice summary opened', {
              practiceSessionId: targetId,
              performanceScore: result?.performance_score ?? null,
            });
            const wasAlreadyOnReport = this.screen === 'final';
            this.showScreen('final');
            // Switched recordings from inside the report: the learner is scrolled down by the
            // recordings list, and the feedback that just replaced everything starts at the top.
            if (wasAlreadyOnReport) {
              setTimeout(() => this.reportContent?.scrollToTop(200), 0);
            }
          } else {
            this.setPracticeStatus(result);
            this.showError(this.i18n.t('That summary is not ready yet.'));
          }
          this.cdRef.detectChanges();
        },
        (error) => {
          console.log('Practice summary fetch failed', error);
          this.isLoadingSummary = false;
          this.openingSummaryId = '';
          // Nothing to show on the report screen we may have opened onto.
          if (!this.analysis && this.screen === 'final') { this.showScreen('main'); }
          this.showError(
            error?.name === 'TimeoutError'
              ? this.i18n.t('That summary is taking too long to load. Please try again.')
              : this.i18n.t('We could not open that performance summary.')
          );
          this.cdRef.detectChanges();
        }
      )
    );
  }


  // Closes the confirmation only — leaves the practice session running.
  dismiss() {
    this.isEndModalOpen = false;
  }

  endAndShowFeedback() {
    this.dismiss();
    // Stops the live call first so its recordings + transcript go up with the session.
    this.finishCall('user_ended');
  }

  /**
   * "End & Discard" — nothing is stored. The session row already exists from step 1, so abandoning it
   * would leave an incomplete attempt on the Action Hub card; we DELETE it instead.
   */
  endAndDiscard() {
    this.dismiss();
    this.isDiscarded = true;
    this.stopCallTicker();
    this.detachBotAudio();
    // Discarding still has to release the mic and close the peer connection.
    if (this.voiceCall.isActive()) {
      this.voiceCall.stop('user_ended');
    }

    const discardedId = this.practiceSessionId;
    mixpanel.track('practice session discarded', {
      practiceSessionId: discardedId,
      durationSeconds: Math.round(this.callElapsedMs / 1000),
    });
    if (discardedId) {
      this.subscriptions.add(
        this.practiceBotService.deletePracticeSession(discardedId).subscribe(
          () => {},
          (error) => {
            // We told them it was discarded; if the delete failed it is still there as an incomplete
            // attempt on their Action Hub card, so say so rather than leaving them misinformed.
            console.log('Practice discard delete failed', error);
            this.showError(this.i18n.t('We could not discard that practice session.'));
            this.cdRef.detectChanges();
          }
        )
      );
    }

    // Wipe this attempt locally too, so nothing from it leaks into the next one.
    this.practiceSessionId = '';
    this.transcript = [];
    this.callElapsedMs = 0;
    this.isEnding = false;
    this.showScreen('main');
  }

  close() {
    // The X in the header used to drop a live call on the floor: no confirmation, no feedback, no
    // record. It now raises the same prompt as End in the voice screen's bottom bar.
    if (this.hasLiveCall()) { this.promptToEnd(); return; }
    this.modalController.dismiss(this.historyChanged ? { changed: true } : null);
  }

  /**
   * The single guard for "skip this on Android".
   *
   * Android is not being tested on hardware, so anything that could move the layout or reorder a
   * permission prompt is kept off it entirely: doing nothing there means Android behaves exactly as
   * it already ships, which is the only outcome that cannot introduce a bug nobody can see. Every
   * platform-specific decision in this component reads this one getter — grep it to find them all.
   *
   * The two things it currently skips are both handled by the platform there anyway: the keyboard by
   * `adjustPan`, and the microphone by the RECORD_AUDIO runtime grant in ensureMicrophonePermission.
   */
  private get isNativeAndroid(): boolean {
    return this.platform.is('cordova') && this.platform.is('android');
  }

  /** A call that a dismissal would throw away — the only case worth interrupting the learner for. */
  private hasLiveCall(): boolean {
    return this.screen === 'voice' && this.voiceCall.isActive();
  }

  private promptToEnd() {
    this.isEndModalOpen = true;
    this.cdRef.detectChanges();
  }

  /**
   * Backdrop taps, swipe-to-dismiss and the Android back button all go through Ionic's `canDismiss`
   * (6.1+). Set on the host element rather than at each call site, so all three places that open this
   * modal get the guard without knowing about it.
   */
  private installDismissGuard() {
    const modalEl: any = (this.elRef?.nativeElement as HTMLElement)?.closest?.('ion-modal');
    if (!modalEl) { return; }   // rendered outside a modal (tests) — nothing to guard
    modalEl.canDismiss = () => this.canDismiss();
  }

  /** False keeps the modal open; the prompt then owns what happens to the call. */
  canDismiss(): Promise<boolean> {
    if (!this.hasLiveCall()) { return Promise.resolve(true); }
    this.promptToEnd();
    return Promise.resolve(false);
  }
}
